// LOGIN-FEEDBACK-01: keep authentication messages separate from action labels.
(() => {
	"use strict";
	if (window.location.pathname !== "/login") return;

	const sections = () =>
		document.querySelectorAll(
			".for-login, .for-email-login, .for-signup, .for-forgot, .for-login-with-email-link"
		);
	const labels = new WeakMap();
	const actionSelector = "form .btn-primary, form .btn-login";
	const actionLabel = (button) =>
		button.classList.contains("btn-login-with-email-link")
			? "Wyślij link do logowania"
			: button.classList.contains("btn-forgot")
			? "Wyślij link"
			: button.classList.contains("btn-signup")
			? "Zarejestruj się"
			: button.classList.contains("btn-login")
			? "ZALOGUJ SIĘ"
			: button.textContent.trim();
	const messages = new Map([
		["Invalid Login. Try again.", "Nieprawidłowy login lub hasło. Spróbuj ponownie."],
		["Invalid credentials, try again.", "Nieprawidłowy login lub hasło. Spróbuj ponownie."],
		[
			"Nieprawidłowe logowanie. Spróbuj ponownie.",
			"Nieprawidłowy login lub hasło. Spróbuj ponownie.",
		],
		["Verifying...", "Sprawdzamy dane…"],
		["Success", "Gotowe."],
		[
			"Instructions Emailed",
			"Jeśli konto istnieje, instrukcja zmiany hasła została wysłana e-mailem.",
		],
		["Login link sent to your email", "Link do logowania został wysłany e-mailem."],
	]);
	let initialized = false;

	const clearFeedback = () =>
		sections().forEach((section) => {
			const feedback = section.querySelector(".ks-login-feedback");
			if (feedback) {
				feedback.hidden = true;
				feedback.textContent = "";
			}
			section
				.querySelectorAll(".page-card-body.invalid")
				.forEach((body) => body.classList.remove("invalid"));
		});

	const init = () => {
		if (initialized || typeof window.login?.set_status !== "function") return;
		initialized = true;
		const nativeSetStatus = window.login.set_status;
		const renderFeedback = (active, message, color) => {
			active.forEach((section) => {
				const buttons = Array.from(section.querySelectorAll(actionSelector));
				if (!buttons.length) return;
				buttons.forEach((button) => {
					button.textContent = labels.get(button) || actionLabel(button);
				});
				const nativeBanner = section.querySelector(".login-error-banner");
				if (nativeBanner) nativeBanner.style.display = "none";
				let feedback = section.querySelector(".ks-login-feedback");
				if (!feedback) {
					feedback = document.createElement("p");
					feedback.className = "ks-login-feedback";
					buttons.at(-1).insertAdjacentElement("afterend", feedback);
				}
				feedback.dataset.state =
					color === "red" ? "error" : color === "green" ? "success" : "info";
				feedback.setAttribute("role", color === "red" ? "alert" : "status");
				feedback.setAttribute("aria-atomic", "true");
				const value = String(message ?? "").trim();
				feedback.textContent = messages.get(value) || value;
				feedback.hidden = !value;
			});
		};
		window.login.set_status = function (message, color) {
			const active = Array.from(sections()).filter(
				(section) => section.getClientRects().length
			);
			active.forEach((section) =>
				section.querySelectorAll(actionSelector).forEach((button) => {
					if (!labels.has(button)) labels.set(button, actionLabel(button));
				})
			);
			const result = nativeSetStatus.apply(this, arguments);
			renderFeedback(active, message, color);
			return result;
		};
		if (typeof window.login.show_error_banner === "function") {
			const nativeShowErrorBanner = window.login.show_error_banner;
			window.login.show_error_banner = function (message) {
				const result = nativeShowErrorBanner.apply(this, arguments);
				renderFeedback(
					Array.from(sections()).filter((section) => section.getClientRects().length),
					message,
					"red"
				);
				return result;
			};
		}
	};

	document.addEventListener(
		"submit",
		(event) => {
			if (event.target.closest(".login-content")) clearFeedback();
		},
		true
	);
	window.addEventListener("hashchange", clearFeedback);
	document.addEventListener("DOMContentLoaded", init, { once: true });
	window.addEventListener("load", init, { once: true });
	window.jQuery?.(document).on("login_rendered.kuckLoginFeedback", init);
	window.frappe?.ready?.(init);
	init();
})();

// CONSENT-01: first-party, provider-neutral cookie boundary.
// This module owns only the choice UI and a same-site cookie. It never loads a
// vendor, creates a vendor request, or infers a choice from a third-party tag.
(() => {
	"use strict";

	const CONSENT_RUNTIME_KEY = "__kuckConsentRuntimeV2";
	const CONSENT_COOKIE = "kuck_consent_v1";
	const CONSENT_SCHEMA_VERSION = 1;
	const CONSENT_POLICY_REVISION =
		"04473cee0459e2a2ec82671e84483ce71c20ab8321f1533009c05ab59d6d0eb1";
	const COOKIE_MAX_AGE = 31536000;
	const CATEGORIES = Object.freeze([
		"necessary",
		"analytics",
		"advertising_measurement",
		"advertising_personalization",
	]);
	const OPTIONAL_CATEGORIES = CATEGORIES.slice(1);
	const CONSENT_FIELDS = Object.freeze(["schema_version", "revision", ...CATEGORIES]);
	const STATUS_MESSAGES = Object.freeze({
		accepted: "Ustawienia plików cookie zapisane.",
		rejected: "Opcjonalne pliki cookie wyłączone.",
		customized: "Własne ustawienia plików cookie zapisane.",
	});

	// A private marker makes an accidental second bundle/include a no-op. The
	// public API remains stable and is not replaced with a second closure.
	if (window[CONSENT_RUNTIME_KEY]?.version === 2) return;

	let snapshot = null;
	let persisted = false;
	let activeRoot = null;
	let returnFocus = null;
	let bound = false;
	const subscribers = new Set();

	const defaultSnapshot = () => ({
		schema_version: CONSENT_SCHEMA_VERSION,
		revision: CONSENT_POLICY_REVISION,
		necessary: "granted",
		analytics: "denied",
		advertising_measurement: "denied",
		advertising_personalization: "denied",
	});

	const freezeSnapshot = (value) => Object.freeze({ ...value });
	const getSnapshot = () => ({ ...snapshot });

	const readCookie = (name) => {
		let source = "";
		try {
			source = typeof document.cookie === "string" ? document.cookie : "";
		} catch (_error) {
			return null;
		}
		const prefix = `${name}=`;
		for (const part of source.split(";")) {
			const candidate = part.trim();
			if (candidate.startsWith(prefix)) return candidate.slice(prefix.length);
		}
		return null;
	};

	const parseCookieSnapshot = () => {
		const encoded = readCookie(CONSENT_COOKIE);
		if (!encoded || encoded.length > 1800) return null;
		try {
			const value = JSON.parse(decodeURIComponent(encoded));
			if (!value || typeof value !== "object" || Array.isArray(value)) return null;
			const fields = Object.keys(value);
			if (
				fields.length !== CONSENT_FIELDS.length ||
				!CONSENT_FIELDS.every((field) => fields.includes(field)) ||
				value.schema_version !== CONSENT_SCHEMA_VERSION ||
				value.revision !== CONSENT_POLICY_REVISION ||
				value.necessary !== "granted"
			)
				return null;
			if (
				OPTIONAL_CATEGORIES.some(
					(category) => !["granted", "denied"].includes(value[category])
				)
			)
				return null;
			return freezeSnapshot(value);
		} catch (_error) {
			return null;
		}
	};

	const writeCookie = (value) => {
		try {
			const secure = window.location?.protocol === "https:" ? "; Secure" : "";
			document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(
				JSON.stringify(value)
			)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
		} catch (_error) {
			// The in-memory decision still gates this page if cookies are disabled.
		}
	};

	const dispatchSnapshot = () => {
		const current = getSnapshot();
		try {
			window.dispatchEvent(new CustomEvent("kuck:consent", { detail: current }));
		} catch (_error) {
			// A missing event API must not turn a consent choice into a page error.
		}
		subscribers.forEach((subscriber) => {
			try {
				subscriber(current);
			} catch (_error) {
				// One future provider cannot prevent remaining subscribers from updating.
			}
		});
	};

	const rootList = () => Array.from(document.querySelectorAll("[data-kuck-consent-root]"));
	const primaryRoot = () => rootList()[0] || null;

	const status = (root, message) => {
		const element = root?.querySelector("[data-kuck-consent-status]");
		if (!element) return;
		element.textContent = message;
		element.hidden = false;
	};

	const setCategoryInputs = (root) => {
		OPTIONAL_CATEGORIES.forEach((category) => {
			const input = root.querySelector(`[data-kuck-consent-category="${category}"]`);
			if (input) input.checked = snapshot[category] === "granted";
		});
	};

	const render = (root) => {
		const banner = root.querySelector("[data-kuck-consent-banner]");
		const dialogShell = root.querySelector("[data-kuck-consent-dialog-shell]");
		const reopen = root.querySelector("[data-kuck-consent-reopen]");
		if (banner) banner.hidden = persisted;
		if (reopen) reopen.hidden = !persisted;
		if (dialogShell && activeRoot !== root) dialogShell.hidden = true;
		setCategoryInputs(root);
	};

	const hiddenOrDisconnected = (element) =>
		!element ||
		element.isConnected === false ||
		element.hidden ||
		element.closest?.("[hidden]");

	// The Home reopen control lives after the footer. When the initial banner
	// disappears, continue in the page content instead of focusing off screen.
	const focusContent = (root) => {
		const content =
			document.querySelector("#kuck-page-content[tabindex]") ||
			document.querySelector("main[tabindex]");
		const target = content || root?.querySelector("[data-kuck-consent-reopen]");
		target?.focus();
	};

	const closeDialog = () => {
		const root = activeRoot;
		const shell = root?.querySelector("[data-kuck-consent-dialog-shell]");
		if (shell) shell.hidden = true;
		const target = returnFocus;
		activeRoot = null;
		returnFocus = null;
		if (!root) return;
		if (hiddenOrDisconnected(target)) focusContent(root);
		else target.focus({ preventScroll: true });
	};

	const openDialog = (root) => {
		const shell = root?.querySelector("[data-kuck-consent-dialog-shell]");
		const dialog = root?.querySelector("[data-kuck-consent-dialog]");
		if (!shell || !dialog) return;
		activeRoot = root;
		returnFocus = document.activeElement;
		setCategoryInputs(root);
		shell.hidden = false;
		dialog.focus();
	};

	const commit = (next, message) => {
		const previousFocus = document.activeElement;
		const hadDialog = Boolean(activeRoot);
		snapshot = freezeSnapshot({
			schema_version: CONSENT_SCHEMA_VERSION,
			revision: CONSENT_POLICY_REVISION,
			necessary: "granted",
			analytics: next.analytics === "granted" ? "granted" : "denied",
			advertising_measurement:
				next.advertising_measurement === "granted" ? "granted" : "denied",
			advertising_personalization:
				next.advertising_personalization === "granted" ? "granted" : "denied",
		});
		persisted = true;
		writeCookie(snapshot);
		const root = primaryRoot();
		if (root) {
			render(root);
			status(root, message);
		}
		closeDialog();
		if (!hadDialog && hiddenOrDisconnected(previousFocus)) {
			focusContent(root);
		}
		dispatchSnapshot();
	};

	const allGranted = () =>
		Object.fromEntries(OPTIONAL_CATEGORIES.map((category) => [category, "granted"]));
	const allDenied = () =>
		Object.fromEntries(OPTIONAL_CATEGORIES.map((category) => [category, "denied"]));
	const customized = (root) =>
		Object.fromEntries(
			OPTIONAL_CATEGORIES.map((category) => {
				const input = root.querySelector(`[data-kuck-consent-category="${category}"]`);
				return [category, input?.checked ? "granted" : "denied"];
			})
		);

	const focusable = (dialog) =>
		Array.from(
			dialog.querySelectorAll(
				'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
			)
		).filter((element) => !element.hidden);

	const actionFor = (target) => {
		const button = target?.closest?.("[data-kuck-consent-action]");
		return button?.getAttribute("data-kuck-consent-action") || null;
	};

	const bindRoot = (root) => {
		if (root.dataset.kuckConsentBound === "true") return;
		root.dataset.kuckConsentBound = "true";
		root.addEventListener("click", (event) => {
			const action = actionFor(event.target);
			if (!action || !root.contains(event.target)) return;
			if (action === "accept") commit(allGranted(), STATUS_MESSAGES.accepted);
			else if (action === "reject" || action === "withdraw")
				commit(allDenied(), STATUS_MESSAGES.rejected);
			else if (action === "customize" || action === "reopen") openDialog(root);
			else if (action === "close") closeDialog();
			else if (action === "save") commit(customized(root), STATUS_MESSAGES.customized);
		});
		const dialog = root.querySelector("[data-kuck-consent-dialog]");
		dialog?.addEventListener("keydown", (event) => {
			if (event.key === "Escape") {
				event.preventDefault();
				closeDialog();
				return;
			}
			if (event.key !== "Tab") return;
			const items = focusable(dialog);
			if (!items.length) {
				event.preventDefault();
				dialog.focus();
				return;
			}
			const first = items[0];
			const last = items[items.length - 1];
			if (
				event.shiftKey &&
				(document.activeElement === first || document.activeElement === dialog)
			) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		});
	};

	const bind = () => {
		if (bound) return;
		bound = true;
		const roots = rootList();
		roots.forEach((root, index) => {
			if (index === 0) {
				bindRoot(root);
				render(root);
				return;
			}
			// A renderer must include one root. Hide an accidental duplicate so it
			// cannot expose duplicate IDs or a second independent decision surface.
			root.hidden = true;
			root.setAttribute("aria-hidden", "true");
			root.dataset.kuckConsentDuplicate = "true";
		});
	};

	const storedSnapshot = parseCookieSnapshot();
	snapshot = freezeSnapshot(storedSnapshot || defaultSnapshot());
	persisted = Boolean(storedSnapshot);
	const api = Object.freeze({
		schema_version: CONSENT_SCHEMA_VERSION,
		revision: CONSENT_POLICY_REVISION,
		getSnapshot,
		has: (category) =>
			CATEGORIES.includes(category) &&
			(category === "necessary" || snapshot[category] === "granted"),
		open: () => {
			bind();
			openDialog(primaryRoot());
		},
		withdraw: () => commit(allDenied(), STATUS_MESSAGES.rejected),
		subscribe: (callback) => {
			if (typeof callback !== "function") return () => {};
			subscribers.add(callback);
			try {
				callback(getSnapshot());
			} catch (_error) {
				// Subscriber errors are isolated from the consent manager.
			}
			return () => subscribers.delete(callback);
		},
	});
	window[CONSENT_RUNTIME_KEY] = { version: 2, revision: CONSENT_POLICY_REVISION };
	window.kuckConsent = api;
	if (document.readyState === "loading")
		document.addEventListener("DOMContentLoaded", bind, { once: true });
	else bind();
})();

(() => {
	const carousel = document.querySelector("[data-kuck-home-carousel]");
	if (!carousel) return;

	const slides = Array.from(carousel.querySelectorAll("[data-kuck-home-slide]"));
	const dots = Array.from(carousel.querySelectorAll("[data-kuck-home-dot]"));
	const previous = carousel.querySelector("[data-kuck-home-prev]");
	const next = carousel.querySelector("[data-kuck-home-next]");
	if (slides.length < 2) return;

	let current = 0;
	let timer = null;
	const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

	const show = (index) => {
		current = (index + slides.length) % slides.length;
		slides.forEach((slide, slideIndex) => {
			const active = slideIndex === current;
			slide.classList.toggle("is-active", active);
			slide.setAttribute("aria-hidden", active ? "false" : "true");
			slide.inert = !active;
		});
		dots.forEach((dot, dotIndex) => {
			const active = dotIndex === current;
			dot.classList.toggle("is-active", active);
			dot.setAttribute("aria-selected", active ? "true" : "false");
			dot.tabIndex = active ? 0 : -1;
		});
	};

	const stop = () => {
		if (timer) window.clearInterval(timer);
		timer = null;
	};

	const start = () => {
		stop();
		if (
			!reducedMotion.matches &&
			document.visibilityState === "visible" &&
			!carousel.contains(document.activeElement)
		) {
			timer = window.setInterval(() => show(current + 1), 6500);
		}
	};

	previous?.addEventListener("click", () => {
		show(current - 1);
		start();
	});
	next?.addEventListener("click", () => {
		show(current + 1);
		start();
	});
	dots.forEach((dot) =>
		dot.addEventListener("click", () => {
			show(Number(dot.dataset.slideIndex));
			start();
		})
	);
	carousel.addEventListener("keydown", (event) => {
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			show(current - 1);
			start();
		} else if (event.key === "ArrowRight") {
			event.preventDefault();
			show(current + 1);
			start();
		}
	});
	carousel.addEventListener("mouseenter", stop);
	carousel.addEventListener("mouseleave", start);
	carousel.addEventListener("focusin", stop);
	carousel.addEventListener("focusout", (event) => {
		if (!carousel.contains(event.relatedTarget)) start();
	});
	document.addEventListener("visibilitychange", start);
	reducedMotion.addEventListener?.("change", start);
	show(0);
	start();
})();

(() => {
	const carousel = document.querySelector("[data-kuck-product-carousel]");
	const initProductCarousel = (root) => {
		if (!root || root.dataset.kuckProductCarouselReady === "true") return;
		const viewport = root.querySelector("[data-kuck-product-viewport]");
		const track = root.querySelector("[data-kuck-product-track]");
		const previous = root.querySelector("[data-kuck-product-prev]");
		const next = root.querySelector("[data-kuck-product-next]");
		if (!viewport || !track || !previous || !next) return;
		root.dataset.kuckProductCarouselReady = "true";
		const cards = Array.from(track.children);
		const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
		const AUTOPLAY_MS = 6000;
		let timer = null;
		let paused = false;
		let positionIndex = 0;

		const updateControls = () => {
			const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth - 1);
			previous.disabled = next.disabled = maxScroll <= 1;
		};

		const move = (direction) => {
			const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
			if (maxScroll <= 1) return;
			const step = cards[0]?.getBoundingClientRect().width || viewport.clientWidth;
			const lastIndex = Math.max(0, Math.round(maxScroll / step));
			positionIndex += direction;
			if (positionIndex > lastIndex) positionIndex = 0;
			if (positionIndex < 0) positionIndex = lastIndex;
			viewport.scrollTo({
				left: Math.min(maxScroll, positionIndex * step),
				behavior: reducedMotion.matches ? "auto" : "smooth",
			});
		};

		const stop = () => {
			if (timer !== null) window.clearInterval(timer);
			timer = null;
		};

		const start = () => {
			stop();
			if (cards.length < 2 || paused || reducedMotion.matches || document.hidden) return;
			timer = window.setInterval(() => move(1), AUTOPLAY_MS);
		};

		previous.addEventListener("click", () => {
			move(-1);
			start();
		});
		next.addEventListener("click", () => {
			move(1);
			start();
		});
		viewport.addEventListener("scroll", updateControls, { passive: true });
		window.addEventListener("resize", updateControls, { passive: true });
		viewport.addEventListener("keydown", (event) => {
			if (event.key === "ArrowLeft") {
				event.preventDefault();
				move(-1);
				start();
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				move(1);
				start();
			}
		});
		root.addEventListener("mouseenter", () => {
			paused = true;
			stop();
		});
		root.addEventListener("mouseleave", () => {
			paused = false;
			start();
		});
		root.addEventListener("focusin", () => {
			paused = true;
			stop();
		});
		root.addEventListener("focusout", (event) => {
			if (!root.contains(event.relatedTarget)) {
				paused = false;
				start();
			}
		});
		document.addEventListener("visibilitychange", start);
		reducedMotion.addEventListener?.("change", start);
		updateControls();
		start();
	};

	window.kuckProductCarouselInit = initProductCarousel;
	if (carousel) initProductCarousel(carousel);
})();

// F14: app-owned cart link and bounded first-party quantity hint.
// The server remains authoritative. This module never observes or rewrites a
// Webshop-owned node. Confirmed mutations supply counts; a scoped GET restores state.
(() => {
	"use strict";

	const CART_RUNTIME_KEY = "__kuckCartNavRuntimeV2";
	if (window[CART_RUNTIME_KEY]?.version === 2) return;

	const readCookie = (name) => {
		try {
			const prefix = `${name}=`;
			for (const part of (document.cookie || "").split(";")) {
				const candidate = part.trim();
				if (candidate.startsWith(prefix)) return candidate.slice(prefix.length);
			}
		} catch (_error) {
			return null;
		}
		return null;
	};

	const countCookie = (name) => {
		const raw = readCookie(name);
		if (!raw || !/^(?:0|[1-9][0-9]{0,5})$/.test(raw)) return null;
		const value = Number(raw);
		return Number.isSafeInteger(value) && value >= 0 && value <= 999999 ? value : null;
	};

	const cartCount = () => {
		const status =
			document.body?.getAttribute("data-kuck-session-status") ||
			document.body?.getAttribute("frappe-session-status");
		const accountCount = countCookie("cart_count");
		const guestCount = countCookie("kuck_guest_cart_count");
		if (status === "guest" || status === "logged-out") return guestCount ?? 0;
		if (status === "logged-in") return accountCount ?? 0;
		return 0;
	};

	const applyCount = (count) => {
		document.querySelectorAll("[data-kuck-cart-nav]").forEach((nav) => {
			const badge = nav.querySelector("[data-kuck-cart-count]");
			const link = nav.matches?.('a[href="/koszyk"]')
				? nav
				: nav.querySelector('a[href="/koszyk"]');
			if (badge) {
				const value = String(count);
				if (badge.textContent !== value) badge.textContent = value;
				if (badge.hidden) badge.hidden = false;
			}
			const label = `Koszyk, ${count} produktów`;
			if (link && link.getAttribute("aria-label") !== label)
				link.setAttribute("aria-label", label);
		});
	};

	const refresh = () => {
		// Also invalidate older reads for native mutations that return no count.
		countGeneration += 1;
		applyCount(cartCount());
		return refreshFromServer();
	};
	let serverRefresh = null;
	let countGeneration = 0;
	const updated = (count) => {
		if (!Number.isSafeInteger(count) || count < 0 || count > 999999) return false;
		countGeneration += 1;
		applyCount(count);
		refreshFromServer();
		return true;
	};
	const refreshFromServer = () => {
		if (typeof window.fetch !== "function") return null;
		if (serverRefresh) return serverRefresh;
		const generation = countGeneration;
		serverRefresh = window
			.fetch("/api/method/kuck_shop.checkout.cart_count.get_cart_count", {
				credentials: "same-origin",
				cache: "no-store",
			})
			.then((response) => (response.ok ? response.json() : null))
			.then((response) => {
				const count = response?.message?.count;
				if (
					generation === countGeneration &&
					Number.isSafeInteger(count) &&
					count >= 0 &&
					count <= 999999
				)
					applyCount(count);
			})
			.catch(() => {})
			.finally(() => {
				serverRefresh = null;
				if (generation !== countGeneration) refreshFromServer();
			});
		return serverRefresh;
	};

	window[CART_RUNTIME_KEY] = { version: 2 };
	window.kuckCartNav = Object.freeze({ refresh, updated });
	window.addEventListener("kuck:cart-updated", (event) => {
		if (!updated(event?.detail?.count)) refresh();
	});
	// Native Frappe/Webshop listing buttons use jQuery AJAX rather than our fetch transport.
	window.jQuery?.(document).on("ajaxComplete.kuckCartCount", (_event, xhr, settings) => {
		const data = settings?.data;
		const method =
			(typeof data === "string" ? new URLSearchParams(data).get("cmd") : data?.cmd) ||
			settings?.url?.split("/api/method/")[1]?.split("?")[0];
		if (
			!/^(webshop\.webshop\.shopping_cart\.cart\.update_cart|kuck_shop\.checkout\.frappe_order\.update_cart)$/.test(
				method || ""
			)
		)
			return;
		if (!xhr.responseJSON?.exc && xhr.status >= 200 && xhr.status < 300)
			if (!updated(xhr.responseJSON?.message?.item_count)) refresh();
	});
	const init = () => {
		refresh();
	};
	if (document.readyState === "loading")
		document.addEventListener("DOMContentLoaded", init, { once: true });
	else init();
})();

(() => {
	"use strict";

	// Turnstile callbacks receive only a token.  Keep it in the bounded hidden
	// field consumed by the app-owned POST; never put it in a URL or log.
	window.kuckReviewTurnstileCallback = (token) => {
		const field = document.querySelector(
			"[data-kuck-product-review-form] [data-kuck-review-turnstile-token]"
		);
		if (field instanceof HTMLInputElement)
			field.value = typeof token === "string" ? token : "";
	};
	window.kuckReviewTurnstileExpired = () => {
		const field = document.querySelector(
			"[data-kuck-product-review-form] [data-kuck-review-turnstile-token]"
		);
		if (field instanceof HTMLInputElement) field.value = "";
	};

	const shell = document.querySelector("[data-ks-menu-shell]");
	const dialog = document.querySelector("[data-ks-menu-dialog]");
	const openButton = document.querySelector("[data-ks-menu-open]");
	const closeButtons = document.querySelectorAll("[data-ks-menu-close]");
	let returnFocus = null;

	const focusable = () =>
		Array.from(
			dialog?.querySelectorAll(
				'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
			) || []
		);

	const closeMenu = () => {
		if (!shell || shell.hidden) return;
		shell.hidden = true;
		document.body.classList.remove("ks-menu-open");
		openButton?.setAttribute("aria-expanded", "false");
		returnFocus?.focus();
	};

	const openMenu = () => {
		if (!shell || !dialog) return;
		returnFocus = document.activeElement;
		shell.hidden = false;
		document.body.classList.add("ks-menu-open");
		openButton?.setAttribute("aria-expanded", "true");
		dialog.focus();
	};

	openButton?.addEventListener("click", openMenu);
	closeButtons.forEach((button) => button.addEventListener("click", closeMenu));
	dialog?.addEventListener("keydown", (event) => {
		if (event.key === "Escape") {
			closeMenu();
			return;
		}
		if (event.key !== "Tab") return;
		const items = focusable();
		if (!items.length) return;
		const first = items[0];
		const last = items[items.length - 1];
		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	});

	const search = document.querySelector("[data-ks-search]");
	const input = search?.querySelector('input[type="search"]');
	const clearButton = search?.querySelector("[data-ks-search-clear]");
	const syncClearButton = () => {
		if (clearButton) clearButton.hidden = !input?.value;
	};
	input?.addEventListener("input", syncClearButton);
	clearButton?.addEventListener("click", () => {
		input.value = "";
		syncClearButton();
		input.focus();
	});
	syncClearButton();

	const initHeaderActions = () => {
		const header = document.querySelector(".ks-site-navbar > .container");
		const collapse = header?.querySelector(".navbar-collapse");
		if (!header || !collapse) return;
		let actions = header.querySelector(".ks-header-actions");
		if (!actions) {
			actions = document.createElement("div");
			actions.className = "ks-header-actions";
			actions.setAttribute("aria-label", "Szybki dostęp");
			header.append(actions);
		}
		[".ks-cart-nav-item > a", ".ks-wishlist-nav > a", ".ks-comparison-nav > a"].forEach(
			(selector) => {
				const link = collapse.querySelector(selector);
				if (!link || link.parentElement === actions) return;
				link.parentElement?.remove();
				actions.append(link);
			}
		);
	};
	initHeaderActions();
	new MutationObserver(initHeaderActions).observe(document.body, {
		childList: true,
		subtree: true,
	});

	/* On mobile the account control is a menu link, not a desktop dropdown.
	 * Keep the destination usable after the dropdown panel is hidden. */
	const routeMobileAccountLinks = () => {
		if (!window.matchMedia?.("(max-width: 1399.98px)").matches) return;
		document
			.querySelectorAll(
				".ks-site-navbar .dropdown-user > .nav-link, .ks-site-navbar #website-post-login .nav-avatar"
			)
			.forEach((link) => {
				if (link.dataset.ksMobileAccountRoute === "true") return;
				link.dataset.ksMobileAccountRoute = "true";
				link.addEventListener("click", (event) => {
					if (!window.matchMedia?.("(max-width: 1399.98px)").matches) return;
					event.preventDefault();
					event.stopPropagation();
					window.location.assign("/konto");
				});
			});
	};
	routeMobileAccountLinks();
	new MutationObserver(routeMobileAccountLinks).observe(document.body, {
		childList: true,
		subtree: true,
	});

	if (window.location.pathname === "/login") {
		if (!document.querySelector(".ks-site-navbar, .kr-page")) {
			fetch("/home", { credentials: "same-origin" })
				.then((response) => (response.ok ? response.text() : ""))
				.then((markup) => {
					if (!markup || document.querySelector(".ks-site-navbar")) return;
					const parsed = new DOMParser().parseFromString(markup, "text/html");
					const shell = parsed.querySelectorAll(
						".ks-site-navbar__top-strip, .ks-site-navbar"
					);
					const loginPage = document.querySelector("#page-login");
					if (!loginPage || !shell.length) return;
					shell.forEach((element) =>
						loginPage.parentElement?.insertBefore(element, loginPage)
					);
				})
				.catch(() => {});
		}
		document.querySelectorAll("img.app-logo, .page-card-head > img").forEach((logo) => {
			if (!logo.alt.trim()) logo.alt = "Kuck";
		});
		{
			const heading = document.querySelector("h1") || document.createElement("h1");
			if (!heading.isConnected) {
				heading.className = "sr-only";
				document.querySelector("main, .page-card")?.prepend(heading);
			}
			const syncLoginHeading = () => {
				heading.textContent =
					window.location.hash === "#signup"
						? "Utwórz konto Kuck"
						: window.location.hash === "#forgot"
						? "Odzyskaj dostęp do konta Kuck"
						: window.location.hash === "#login-with-email-link"
						? "Logowanie przez link"
						: "Logowanie do konta Kuck";
			};
			syncLoginHeading();
			window.addEventListener("hashchange", syncLoginHeading);
		}
		document.querySelectorAll(".page-card-head h4").forEach((heading) => {
			const sectionHeading = document.createElement("h2");
			Array.from(heading.attributes).forEach(({ name, value }) =>
				sectionHeading.setAttribute(name, value)
			);
			sectionHeading.replaceChildren(...heading.childNodes);
			heading.replaceWith(sectionHeading);
			sectionHeading.textContent = sectionHeading.closest(".for-signup")
				? "Utwórz konto"
				: sectionHeading.closest(".for-forgot")
				? "Odzyskaj dostęp do konta"
				: sectionHeading.closest(".for-login-with-email-link")
				? "Logowanie przez link"
				: "Zaloguj się do swojego konta";
		});
		document.querySelectorAll(".forgot-password-message a").forEach((link) => {
			link.textContent = "Nie pamiętasz hasła?";
		});
		document
			.querySelectorAll(".for-login .btn-login, .for-email-login .btn-login")
			.forEach((button) => {
				button.textContent = "ZALOGUJ SIĘ";
			});
		document.querySelectorAll(".btn-login-option").forEach((button) => {
			button.textContent = "Logowanie przez link";
		});
		document.querySelectorAll('.sign-up-message a[href="#signup"]').forEach((link) => {
			link.textContent = "Zarejestruj się";
		});
		document
			.querySelectorAll(
				'.sign-up-message a[href="#login"], .for-forgot a[href="#login"], .for-login-with-email-link a[href="#login"]'
			)
			.forEach((link) => {
				link.textContent = link.closest(".for-signup")
					? "Masz już konto? Zaloguj się"
					: "Wróć do logowania";
			});
		document.querySelectorAll(".for-signup .btn-signup").forEach((button) => {
			button.textContent = "Zarejestruj się";
		});
		document.querySelectorAll(".for-forgot .btn-forgot").forEach((button) => {
			button.textContent = "Wyślij link";
		});
		document
			.querySelectorAll(".for-login-with-email-link .btn-login-with-email-link")
			.forEach((button) => {
				button.textContent = "Wyślij link do logowania";
			});
		const translateLogin = () => {
			const replacements = new Map([
				["Sign In", "Zaloguj się do swojego konta"],
				["Continue", "ZALOGUJ SIĘ"],
				["Create Account", "Zarejestruj się"],
				["Send Link", "Wyślij link"],
				["Forgot password?", "Nie pamiętasz hasła?"],
				["Login With Email Link", "Logowanie przez link"],
				["Not a member? Sign up", "Nie masz konta? Zarejestruj się"],
			]);
			document
				.querySelectorAll(
					".for-login .page-card-head h2, .for-login .page-card-head h4, .for-login button, .for-login a, .for-signup button, .for-forgot button"
				)
				.forEach((element) => {
					const value = element.textContent?.trim();
					if (value && replacements.has(value))
						element.textContent = replacements.get(value);
				});
		};
		translateLogin();
		document.querySelectorAll(".for-login, .for-signup, .for-forgot").forEach((section) => {
			new MutationObserver(translateLogin).observe(section, {
				childList: true,
				subtree: true,
			});
		});
		const loginLabels = {
			forgot_email: "Adres e-mail do odzyskania hasła",
			login_with_email_link_email: "Adres e-mail do linku logowania",
		};
		Object.entries(loginLabels).forEach(([id, label]) => {
			document.getElementById(id)?.setAttribute("aria-label", label);
		});
		document.querySelectorAll(".toggle-password").forEach((toggle) => {
			const target = toggle.getAttribute("toggle") || "";
			if (!/^#[A-Za-z][\w:.-]*$/.test(target)) return;
			const controlledInput = document.getElementById(target.slice(1));
			if (
				!(controlledInput instanceof HTMLInputElement) ||
				!["password", "text"].includes(controlledInput.type)
			)
				return;
			const syncPasswordState = () => {
				const visible = controlledInput.type === "text";
				toggle.setAttribute("aria-pressed", String(visible));
				toggle.setAttribute("aria-label", visible ? "Ukryj hasło" : "Pokaż hasło");
			};
			toggle.classList.remove("text-muted");
			toggle.setAttribute("role", "button");
			toggle.setAttribute("tabindex", "0");
			toggle.setAttribute("aria-controls", controlledInput.id);
			syncPasswordState();
			new MutationObserver(syncPasswordState).observe(controlledInput, {
				attributes: true,
				attributeFilter: ["type"],
			});
			toggle.addEventListener("keydown", (event) => {
				if (event.key !== "Enter" && event.key !== " ") return;
				event.preventDefault();
				toggle.click();
			});
		});
		document
			.querySelectorAll(".login-divider.text-muted")
			.forEach((divider) => divider.classList.remove("text-muted"));
		document.querySelectorAll(".page-card input[required]").forEach((field) => {
			field.addEventListener("invalid", () => {
				field.setCustomValidity(
					field.validity.typeMismatch
						? "Podaj prawidłowy adres e-mail."
						: "Uzupełnij to pole."
				);
			});
			field.addEventListener("input", () => field.setCustomValidity(""));
		});
	}

	// The legacy path guard was `window.location.pathname === "/contact"`.
	// The public body marker is the stable route contract for this enhancement.
	const enhanceContactForm = () => {
		const contactPage = document.querySelector('body[data-path="contact"]');
		if (!contactPage || contactPage.getAttribute("data-ks-contact-form-enhanced") === "true")
			return;

		const contactRoot = contactPage.querySelector(".contact-content") || contactPage;
		const fields = [
			{
				selector: 'select[name="subject"]',
				id: "kuck-contact-subject",
				label: "Temat wiadomości",
			},
			{
				selector: 'input[name="email"]',
				id: "kuck-contact-email",
				label: "Adres e-mail",
			},
			{
				selector: 'textarea[name="message"]',
				id: "kuck-contact-message",
				label: "Wiadomość",
			},
		].map((definition) => ({
			...definition,
			field: contactRoot.querySelector(definition.selector),
		}));
		if (fields.some(({ field }) => !field)) return;
		if (
			fields.some(({ field, id }) => {
				const existing = document.getElementById(id);
				return existing && existing !== field;
			})
		)
			return;

		fields.forEach(({ field, id, label }) => {
			const previousId = field.id;
			field.id = id;
			field.setAttribute("aria-label", label);
			let labelElement = previousId
				? Array.from(contactRoot.querySelectorAll("label")).find(
						(candidate) => candidate.htmlFor === previousId
				  )
				: null;
			if (!(labelElement instanceof HTMLLabelElement) || labelElement.contains(field)) {
				labelElement = document.createElement("label");
				labelElement.dataset.ksContactLabel = "true";
				(field.closest("label") || field).insertAdjacentElement(
					"beforebegin",
					labelElement
				);
			}
			labelElement.htmlFor = id;
			labelElement.textContent = label;
		});

		const emailField = fields.find(({ selector }) => selector === 'input[name="email"]').field;
		emailField.setAttribute("type", "email");
		emailField.setAttribute("autocomplete", "email");

		const messageField = fields.find(
			({ selector }) => selector === 'textarea[name="message"]'
		).field;
		const orderFromQuery = () => {
			if (typeof window.URLSearchParams !== "function") return null;
			const value = new URLSearchParams(window.location.search).get("order");
			if (typeof value !== "string") return null;
			const normalized = value.replace(/[\u0000-\u001f\u007f]/g, " ").trim();
			return normalized && normalized.length <= 140 ? normalized : null;
		};
		const orderNumber = orderFromQuery();
		if (orderNumber) {
			const orderPrefix = `Numer zamówienia: ${orderNumber}`;
			if (!messageField.value.trim()) messageField.value = orderPrefix;
			else if (!messageField.value.includes(orderPrefix))
				messageField.value = `${orderPrefix}\n\n${messageField.value}`;
		}
		const descriptionId = "kuck-contact-message-description";
		let messageDescription = document.getElementById(descriptionId);
		if (messageDescription && !contactRoot.contains(messageDescription)) return;
		if (!(messageDescription instanceof HTMLElement)) {
			messageDescription = document.createElement("p");
			messageDescription.id = descriptionId;
			messageDescription.dataset.ksContactDescription = "true";
			messageDescription.className = "form-text";
			messageDescription.textContent = "Opisz, w czym możemy pomóc.";
			messageField.insertAdjacentElement("afterend", messageDescription);
		}
		const describedBy = new Set(
			(messageField.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean)
		);
		describedBy.add(descriptionId);
		messageField.setAttribute("aria-describedby", Array.from(describedBy).join(" "));
		contactPage.setAttribute("data-ks-contact-form-enhanced", "true");
	};
	enhanceContactForm();

	const zoomView = document.querySelector(".image-zoom-view");
	const syncZoomImageAlt = () => {
		const sourceAlt =
			document.querySelector(".product-image .website-image")?.alt || "Produkt Kuck";
		zoomView?.querySelectorAll("img:not([alt])").forEach((image) => {
			image.alt = sourceAlt;
		});
	};
	if (zoomView) {
		new MutationObserver(syncZoomImageAlt).observe(zoomView, { childList: true });
	}

	const newsletterCsrfToken = (form) =>
		form.querySelector('input[name="csrf_token"]')?.value ||
		document.querySelector('meta[name="csrf-token"]')?.content ||
		(window.frappe?.csrf_token !== "None" ? window.frappe?.csrf_token : "");
	const enableNewsletterSubmit = (form, submit) => {
		const csrfToken = newsletterCsrfToken(form);
		if (typeof window.fetch !== "function" || typeof csrfToken !== "string" || !csrfToken)
			return false;
		submit.disabled = false;
		submit.removeAttribute("aria-disabled");
		form.removeAttribute("aria-disabled");
		return true;
	};

	const enhanceNewsletterForms = () => {
		document.querySelectorAll("[data-kuck-newsletter-form]").forEach((form) => {
			if (form.getAttribute("data-kuck-newsletter-enhanced") === "true") return;
			const status = form.querySelector("[data-kuck-newsletter-status]");
			const submit = form.querySelector('button[type="submit"]');
			if (!(form instanceof HTMLFormElement) || !(submit instanceof HTMLButtonElement))
				return;
			form.setAttribute("data-kuck-newsletter-enhanced", "true");
			const tryEnable = () => enableNewsletterSubmit(form, submit);
			tryEnable();
			window.setTimeout(tryEnable, 0);
			form.addEventListener("submit", async (event) => {
				if (typeof window.fetch !== "function") return;
				event.preventDefault();
				if (typeof form.reportValidity === "function" && !form.reportValidity()) return;
				const csrfToken = newsletterCsrfToken(form);
				if (typeof csrfToken !== "string" || !csrfToken) {
					if (status) {
						status.setAttribute("role", "alert");
						status.textContent =
							"Nie udało się wysłać formularza. Odśwież stronę i spróbuj ponownie.";
					}
					return;
				}
				submit.disabled = true;
				form.setAttribute("aria-busy", "true");
				if (status) {
					status.setAttribute("role", "status");
					status.textContent = "Wysyłamy formularz…";
				}
				try {
					const response = await window.fetch(
						form.getAttribute("action") ||
							"/api/method/kuck_shop.newsletter.api.subscribe",
						{
							method: "POST",
							credentials: "same-origin",
							headers: {
								Accept: "application/json",
								"X-Frappe-CSRF-Token": csrfToken,
							},
							body: new URLSearchParams(new FormData(form)),
						}
					);
					const payload = await response.json().catch(() => null);
					const result = payload?.message;
					if (!response.ok || result?.status !== "accepted")
						throw new Error("REQUEST_REJECTED");
					if (status) status.textContent = result.message;
					form.reset();
				} catch (_error) {
					if (status) {
						status.setAttribute("role", "alert");
						status.textContent =
							"Nie udało się wysłać formularza. Spróbuj ponownie później.";
					}
				} finally {
					submit.disabled = false;
					form.removeAttribute("aria-busy");
				}
			});
		});
	};
	enhanceNewsletterForms();

	const enhanceNewsletterActionForms = () => {
		const tokenFromFragment = () => {
			if (typeof window.URLSearchParams !== "function") return null;
			const fragment = window.location.hash.startsWith("#")
				? window.location.hash.slice(1)
				: "";
			const token = new URLSearchParams(fragment).get("token");
			return typeof token === "string" && /^[A-Za-z0-9_-]{32,128}$/.test(token)
				? token
				: null;
		};
		const showInvalidNewsletterLink = (form, status) => {
			const message = "Ten link jest nieprawidłowy albo wygasł.";
			if (status) status.textContent = message;
			const main = document.querySelector(".ks-newsletter-result");
			const pageStatus = main?.querySelector("[data-kuck-newsletter-page-status]");
			if (pageStatus) pageStatus.textContent = message;
			const heading = main?.querySelector("h1");
			if (heading) heading.textContent = "Link newslettera";
			form.remove();
		};
		document.querySelectorAll("[data-kuck-newsletter-action-form]").forEach((form) => {
			if (form.getAttribute("data-kuck-newsletter-action-enhanced") === "true") return;
			const status = form.querySelector("[data-kuck-newsletter-action-status]");
			const submit = form.querySelector('button[type="submit"]');
			const tokenField = form.querySelector('input[name="token"]');
			if (
				!(form instanceof HTMLFormElement) ||
				!(submit instanceof HTMLButtonElement) ||
				!(tokenField instanceof HTMLInputElement)
			)
				return;
			const token = tokenFromFragment();
			if (!token) {
				showInvalidNewsletterLink(form, status);
				return;
			}
			tokenField.value = token;
			try {
				window.history.replaceState({}, "", "/newsletter");
			} catch (_error) {
				// URL cleanup is best effort; the token remains in the POST body.
			}
			form.setAttribute("data-kuck-newsletter-action-enhanced", "true");
			const tryEnable = () => enableNewsletterSubmit(form, submit);
			tryEnable();
			window.setTimeout(tryEnable, 0);
			form.addEventListener("submit", async (event) => {
				if (typeof window.fetch !== "function") return;
				event.preventDefault();
				const csrfToken = newsletterCsrfToken(form);
				if (typeof csrfToken !== "string" || !csrfToken) {
					if (status) {
						status.setAttribute("role", "alert");
						status.textContent =
							"Nie udało się wykonać operacji. Odśwież stronę i spróbuj ponownie.";
					}
					return;
				}
				submit.disabled = true;
				form.setAttribute("aria-busy", "true");
				if (status) status.textContent = "Przetwarzamy żądanie…";
				try {
					const response = await window.fetch(form.action, {
						method: "POST",
						credentials: "same-origin",
						headers: {
							Accept: "application/json",
							"X-Frappe-CSRF-Token": csrfToken,
						},
						body: new URLSearchParams(new FormData(form)),
					});
					const payload = await response.json().catch(() => null);
					const result = payload?.message;
					if (
						!response.ok ||
						!["confirmed", "unsubscribed", "invalid"].includes(result?.status)
					)
						throw new Error("REQUEST_REJECTED");
					const main = document.querySelector(".ks-newsletter-result");
					const pageStatus = main?.querySelector("[data-kuck-newsletter-page-status]");
					if (status) status.textContent = result.message;
					if (pageStatus) pageStatus.textContent = result.message;
					form.remove();
					const heading = main?.querySelector("h1");
					if (heading) {
						heading.textContent =
							result.status === "confirmed"
								? "Zapis potwierdzony"
								: result.status === "unsubscribed"
								? "Wypisanie przyjęte"
								: "Link newslettera";
					}
					try {
						window.history.replaceState({}, "", "/newsletter");
					} catch (_error) {
						// URL cleanup is best effort; the action has already completed.
					}
				} catch (_error) {
					if (status) {
						status.setAttribute("role", "alert");
						status.textContent =
							"Nie udało się wykonać operacji. Spróbuj ponownie później.";
					}
				} finally {
					submit.disabled = false;
					form.removeAttribute("aria-busy");
				}
			});
		});
	};
	enhanceNewsletterActionForms();

	const reviewCsrfToken = (form) =>
		window.frappe?.csrf_token ||
		form.querySelector('input[name="csrf_token"]')?.value ||
		document.querySelector('meta[name="csrf-token"]')?.content;
	const enhanceProductReviewForms = () => {
		document.querySelectorAll("[data-kuck-product-review-form]").forEach((form) => {
			if (!(form instanceof HTMLFormElement) || form.dataset.kuckReviewEnhanced === "true")
				return;
			const submit = form.querySelector('button[type="submit"]');
			const status = form.querySelector("[data-kuck-product-review-status]");
			const tokenField = form.querySelector("[data-kuck-review-turnstile-token]");
			if (
				!(submit instanceof HTMLButtonElement) ||
				!(tokenField instanceof HTMLInputElement)
			)
				return;
			form.dataset.kuckReviewEnhanced = "true";
			form.addEventListener("submit", async (event) => {
				event.preventDefault();
				if (typeof form.reportValidity === "function" && !form.reportValidity()) return;
				const csrfToken = reviewCsrfToken(form);
				if (typeof csrfToken !== "string" || !csrfToken || !tokenField.value) {
					if (status) {
						status.setAttribute("role", "alert");
						status.textContent = "Nie udało się wysłać opinii. Spróbuj ponownie.";
					}
					return;
				}
				submit.disabled = true;
				form.setAttribute("aria-busy", "true");
				if (status) {
					status.setAttribute("role", "status");
					status.textContent = "Wysyłamy opinię…";
				}
				try {
					const response = await window.fetch(form.action, {
						method: "POST",
						credentials: "same-origin",
						headers: {
							Accept: "application/json",
							"X-Frappe-CSRF-Token": csrfToken,
						},
						body: new FormData(form),
					});
					const payload = await response.json().catch(() => null);
					const result = payload?.message;
					if (!response.ok || result?.status !== "accepted")
						throw new Error("REVIEW_REJECTED");
					if (status) status.textContent = result.message;
					window.setTimeout(() => window.location.reload(), 250);
				} catch (_error) {
					if (status) {
						status.setAttribute("role", "alert");
						status.textContent =
							"Nie udało się wysłać opinii. Spróbuj ponownie później.";
					}
					submit.disabled = false;
				} finally {
					form.removeAttribute("aria-busy");
				}
			});
		});
	};
	enhanceProductReviewForms();

	const replaceTextSeparatorRuns = (description) => {
		const textNodes = [];
		const walker = document.createTreeWalker(description, NodeFilter.SHOW_TEXT);
		let node;
		while ((node = walker.nextNode())) {
			if (node.parentElement?.closest("script, style")) continue;
			if (/_{12,}/.test(node.nodeValue || "")) textNodes.push(node);
		}
		textNodes.forEach((textNode) => {
			const value = textNode.nodeValue || "";
			const fragment = document.createDocumentFragment();
			let cursor = 0;
			value.replace(/_{12,}/g, (run, offset) => {
				if (offset > cursor) {
					fragment.append(document.createTextNode(value.slice(cursor, offset)));
				}
				fragment.append(document.createElement("hr"));
				cursor = offset + run.length;
				return "";
			});
			if (cursor < value.length) {
				fragment.append(document.createTextNode(value.slice(cursor)));
			}
			textNode.replaceWith(fragment);
		});
	};
	const normalizeProductDescriptionSeparators = () => {
		document
			.querySelectorAll(".ks-webshop-product__description")
			.forEach(replaceTextSeparatorRuns);
	};
	const initProductDescriptionToggles = (root = document) => {
		const descriptions = [];
		if (root instanceof Element && root.matches("[data-ks-description]")) {
			descriptions.push(root);
		}
		descriptions.push(...(root.querySelectorAll?.("[data-ks-description]") || []));
		descriptions.forEach((description) => {
			if (description.dataset.ksDescriptionInitialized === "true") return;
			const content = description.querySelector("[data-ks-description-content]");
			const toggle = description.querySelector("[data-ks-description-toggle]");
			if (!(content instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement))
				return;
			description.dataset.ksDescriptionInitialized = "true";
			const media = window.matchMedia?.("(max-width: 767px)");
			const isMobile = () => media?.matches ?? false;
			const setExpanded = (expanded) => {
				description.dataset.ksDescriptionExpanded = String(expanded);
				toggle.setAttribute("aria-expanded", String(expanded));
				toggle.textContent = expanded ? "Zwiń opis" : "Pokaż pełny opis";
			};
			const sync = (preserveExpanded = false) => {
				const wasExpanded =
					preserveExpanded && description.dataset.ksDescriptionExpanded === "true";
				description.removeAttribute("data-ks-description-enhanced");
				description.removeAttribute("data-ks-description-expanded");
				toggle.hidden = true;
				toggle.setAttribute("aria-expanded", "false");
				toggle.textContent = "Pokaż pełny opis";
				if (!isMobile()) return;
				const rootFontSize =
					parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
				const collapsedHeight = 18 * rootFontSize;
				if (content.scrollHeight <= collapsedHeight + 24) return;
				description.dataset.ksDescriptionEnhanced = "true";
				setExpanded(wasExpanded);
				toggle.hidden = false;
			};
			const resyncAfterLayout = () => sync(true);
			toggle.addEventListener("click", () => {
				if (toggle.hidden) return;
				setExpanded(description.dataset.ksDescriptionExpanded !== "true");
			});
			sync(false);
			if (media?.addEventListener) media.addEventListener("change", () => sync(false));
			else window.addEventListener("resize", resyncAfterLayout);
			window.addEventListener("load", resyncAfterLayout, { once: true });
			content.querySelectorAll("img").forEach((image) => {
				image.addEventListener("load", resyncAfterLayout, { once: true });
			});
			document.fonts?.ready.then(resyncAfterLayout);
			if (typeof ResizeObserver !== "undefined") {
				const observer = new ResizeObserver(() => {
					if (!description.dataset.ksDescriptionEnhanced) sync(true);
				});
				observer.observe(content);
			}
		});
	};
	const labelAccessibleLinks = (root = document) => {
		const links = [];
		if (root instanceof Element && root.matches("a")) links.push(root);
		links.push(...(root.querySelectorAll?.("a") || []));
		links.forEach((link) => {
			if (
				link.getAttribute("aria-label") ||
				link.textContent.trim() ||
				link.querySelector("img[alt]")
			)
				return;
			let pathname;
			try {
				pathname = new URL(link.getAttribute("href") || "", window.location.origin)
					.pathname;
			} catch {
				return;
			}
			if (pathname === "/cart") {
				link.setAttribute("aria-label", "Przejdź do koszyka");
				return;
			}
			if (link.classList.contains("stretched-link")) {
				const heading = link
					.closest(".card")
					?.querySelector("h2, h3, h4, .card-title")
					?.textContent.trim();
				link.setAttribute(
					"aria-label",
					heading ? `Przejdź do: ${heading}` : "Przejdź do szczegółów"
				);
			}
		});
	};
	const improveHomeCardLinks = (root = document) => {
		if (document.body.dataset.path !== "kuck-home") return;
		const links = [];
		if (root instanceof Element && root.matches(".card > a.stretched-link")) {
			links.push(root);
		}
		links.push(...(root.querySelectorAll?.(".card > a.stretched-link") || []));
		links.forEach((link) => {
			if (link.classList.contains("ks-home-card-action")) return;
			const title = link.closest(".card")?.querySelector(".card-title")?.textContent.trim();
			link.classList.add("ks-home-card-action");
			link.removeAttribute("aria-label");
			link.textContent = title ? `Zobacz: ${title}` : "Zobacz szczegóły";
		});
	};
	const removeInactiveQuoteLinks = (root = document) => {
		const links = [];
		if (root instanceof Element && root.matches("a[href]")) links.push(root);
		links.push(...(root.querySelectorAll?.("a[href]") || []));
		links.forEach((link) => {
			let pathname;
			try {
				pathname = new URL(link.getAttribute("href") || "", window.location.origin)
					.pathname;
			} catch {
				return;
			}
			if (
				pathname === "/cart" &&
				link.closest("#product-listing") &&
				link.querySelector(
					":scope > .btn-add-to-cart-list.go-to-cart, :scope > .btn-add-to-cart-list.go-to-cart-grid"
				)
			) {
				link.remove();
			}
		});
	};
	const repairCatalogueSearchDescription = (root = document) => {
		const searchBox = root.matches?.("#search-box[aria-describedby='button-addon2']")
			? root
			: root.querySelector?.("#search-box[aria-describedby='button-addon2']");
		if (searchBox && !document.getElementById("button-addon2"))
			searchBox.removeAttribute("aria-describedby");
		const results = root.matches?.("#search-results-container")
			? root
			: root.querySelector?.("#search-results-container");
		if (results?.hasAttribute("aria-labelledby") && !results.hasAttribute("role")) {
			results.setAttribute("role", "region");
		}
	};
	const syncCatalogueSearchValue = (root = document) => {
		if (window.location.pathname !== "/all-products") return;
		const value = new URLSearchParams(window.location.search).get("search")?.trim();
		if (!value || new TextEncoder().encode(value).length > 128) return;
		const searchBox = root.matches?.("#search-box")
			? root
			: root.querySelector?.("#search-box");
		if (searchBox && !searchBox.value) searchBox.value = value;
	};
	const removeGuestSyntheticCatalogueMarkers = (root = document) => {
		if (document.body.getAttribute("frappe-session-status") !== "logged-out") return;
		const elements = [];
		if (root instanceof Element) elements.push(root);
		elements.push(...(root.querySelectorAll?.("#product-listing *") || []));
		const syntheticAttributes = [
			"data-item-code",
			"data-itemcode",
			"data-product-code",
			"data-item-name",
		];
		elements.forEach((element) => {
			if (!element.closest?.("#product-listing")) return;
			if (
				element.classList.contains("product-code") &&
				element.textContent?.includes("KUCK-PS-")
			) {
				element.remove();
				return;
			}
			syntheticAttributes.forEach((attribute) => {
				if (element.getAttribute(attribute)?.trim().startsWith("KUCK-PS-")) {
					element.removeAttribute(attribute);
				}
			});
		});
	};

	/*
	 * Product quick view is intentionally a thin enhancement of the public
	 * Webshop cards.  It never asks Webshop for another projection: every value
	 * is read from the visible card and an incomplete/ambiguous card keeps its
	 * ordinary product link only.
	 */
	const QUICK_VIEW_PRIVATE_MARKER = "KUCK-PS-";
	const QUICK_VIEW_TRIGGER_ATTRIBUTE = "data-kuck-quick-view-trigger";
	const QUICK_VIEW_READY_ATTRIBUTE = "data-kuck-quick-view-ready";
	const QUICK_VIEW_BUSY_DISABLED_ATTRIBUTE = "data-kuck-quick-view-busy-disabled";
	const QUICK_VIEW_HTTP_PROTOCOLS = new Set(["http:", "https:"]);
	const QUICK_VIEW_PRIVATE_PATH =
		/^\/(?:api|app|desk|login|logout|signup|account|konto|checkout|koszyk|cart|serwis|porownaj|wishlist|compare|all-products)(?:\/|$)/i;
	const QUICK_VIEW_PRODUCT_LINK_SELECTOR = [
		"a.product-link",
		"a.product-list-link",
		".card-img-container > a[href]",
		".list-image > a[href]",
		".product-title",
		".product-name",
		".card-title",
		"[itemprop='name']",
		".col-8 > a[href]",
	].join(", ");
	const QUICK_VIEW_ACTION_LINK_SELECTOR = [
		".cart-action-container",
		"a[class*='-card-action']",
	].join(", ");
	const QUICK_VIEW_VISIBILITY_ATTRIBUTE_FILTER = [
		"class",
		"style",
		"src",
		"hidden",
		"aria-hidden",
		"aria-busy",
	];
	const QUICK_VIEW_PATH_CHARACTER = /^[\p{L}\p{N}._~!$&'()*+,;=:@%\/-]+$/u;
	let quickViewSequence = 0;

	const isCataloguePage = () => {
		const path = window.location.pathname.replace(/\/+$/, "") || "/";
		return path === "/all-products" || document.body?.dataset.path === "all-products";
	};
	const isHomeProductPage = () => {
		const path = window.location.pathname.replace(/\/+$/, "") || "/";
		return path === "/home" || document.body?.dataset.path === "kuck-home";
	};
	const isProductCardPage = () => isCataloguePage() || isHomeProductPage();

	const hasPrivateQuickViewMarker = (value) =>
		typeof value === "string" && value.toUpperCase().includes(QUICK_VIEW_PRIVATE_MARKER);

	const isVisibleQuickViewElement = (element) => {
		if (!(element instanceof HTMLElement) || element.hidden) return false;
		if (element.closest("[hidden], [aria-hidden='true']")) return false;
		const style = window.getComputedStyle?.(element);
		return !style || (style.display !== "none" && style.visibility !== "hidden");
	};

	const visibleQuickViewText = (element, maximum = 240) => {
		if (!isVisibleQuickViewElement(element)) return null;
		const value = element.textContent?.replace(/\s+/g, " ").trim() || "";
		if (!value || value.length > maximum || hasPrivateQuickViewMarker(value)) return null;
		return value;
	};

	const resolvePublicProductHref = (link) => {
		if (!(link instanceof HTMLAnchorElement)) return null;
		const rawHref = link.getAttribute("href")?.trim() || "";
		if (!rawHref || rawHref === "#" || rawHref.startsWith("#")) return null;
		if (hasPrivateQuickViewMarker(rawHref)) return null;
		let url;
		try {
			url = new URL(rawHref, window.location.origin);
		} catch (_error) {
			return null;
		}
		if (
			url.origin !== window.location.origin ||
			!QUICK_VIEW_HTTP_PROTOCOLS.has(url.protocol) ||
			url.username ||
			url.password ||
			url.search ||
			url.hash ||
			QUICK_VIEW_PRIVATE_PATH.test(url.pathname) ||
			!QUICK_VIEW_PATH_CHARACTER.test(url.pathname)
		)
			return null;
		if (url.pathname.length < 2 || url.pathname.includes("//")) return null;
		if (/%(?:00|2e|2f|5c)/i.test(url.pathname)) return null;
		let decodedPath;
		try {
			decodedPath = decodeURIComponent(url.pathname);
		} catch (_error) {
			return null;
		}
		if (
			decodedPath.length < 2 ||
			hasPrivateQuickViewMarker(decodedPath) ||
			decodedPath.includes("\\") ||
			/[\u0000-\u001f\u007f<>"`]/.test(decodedPath) ||
			decodedPath
				.split("/")
				.slice(1)
				.some((segment) => !segment || segment === "." || segment === "..")
		)
			return null;
		return url.href;
	};

	const readQuickViewLink = (card) => {
		const productAnchors = new Set();
		const collectProductAnchor = (element) => {
			const anchor =
				element instanceof HTMLAnchorElement ? element : element.closest?.("a[href]");
			if (
				anchor instanceof HTMLAnchorElement &&
				!anchor.closest(QUICK_VIEW_ACTION_LINK_SELECTOR)
			)
				productAnchors.add(anchor);
		};
		card.querySelectorAll(QUICK_VIEW_PRODUCT_LINK_SELECTOR).forEach(collectProductAnchor);
		card.querySelectorAll("img.card-img, img.website-image, img[itemprop='image']").forEach(
			collectProductAnchor
		);
		const hrefs = new Set();
		productAnchors.forEach((link) => {
			const href = resolvePublicProductHref(link);
			if (href) hrefs.add(href);
		});
		return hrefs.size === 1 ? hrefs.values().next().value : null;
	};

	const readQuickViewImage = (card, name) => {
		const image = card.querySelector("img.card-img, img.website-image, img[itemprop='image']");
		if (!(image instanceof HTMLImageElement) || !isVisibleQuickViewElement(image)) return null;
		const rawSource = image.getAttribute("src")?.trim() || image.currentSrc || "";
		if (!rawSource || hasPrivateQuickViewMarker(rawSource)) return null;
		let source;
		try {
			source = new URL(rawSource, window.location.origin);
		} catch (_error) {
			return null;
		}
		if (
			source.origin !== window.location.origin ||
			!QUICK_VIEW_HTTP_PROTOCOLS.has(source.protocol) ||
			source.username ||
			source.password ||
			source.hash
		)
			return null;
		let decodedSourcePath;
		try {
			decodedSourcePath = decodeURIComponent(source.pathname);
		} catch (_error) {
			return null;
		}
		if (hasPrivateQuickViewMarker(decodedSourcePath)) return null;
		const alt = image.getAttribute("alt")?.trim() || name;
		if (!alt || hasPrivateQuickViewMarker(alt)) return null;
		return { alt, src: source.href };
	};

	const QUICK_VIEW_EXCLUDED_PRICE_NODE =
		"s, del, .striked-price, .product-info-green, .in-stock, .out-of-stock, [data-old-price], [aria-label*='Poprzednia'], [data-omnibus], .omnibus, [hidden], [aria-hidden='true']";
	const QUICK_VIEW_FORBIDDEN_PRICE_TEXT =
		/\b(?:old[_ -]?price|rabat|discount|omnibus|off|taniej)\b/i;
	const QUICK_VIEW_DEFAULT_AVAILABILITY = "Dostępność wymaga potwierdzenia";
	const readQuickViewPrice = (card) => {
		const priceRoot = card.querySelector(
			".product-price, .ks-price__current, [itemprop='offers']"
		);
		if (!(priceRoot instanceof HTMLElement) || !isVisibleQuickViewElement(priceRoot))
			return null;
		const current = priceRoot.matches(".ks-price__current")
			? priceRoot
			: priceRoot.querySelector(".ks-price__current, .current-price, [data-current-price]");
		if (current) {
			const value = visibleQuickViewText(current, 120);
			return value && !QUICK_VIEW_FORBIDDEN_PRICE_TEXT.test(value) ? value : null;
		}
		const walker = document.createTreeWalker(priceRoot, NodeFilter.SHOW_TEXT);
		const pieces = [];
		let node;
		while ((node = walker.nextNode())) {
			if (node.parentElement?.closest(QUICK_VIEW_EXCLUDED_PRICE_NODE)) continue;
			const value = node.nodeValue?.replace(/\s+/g, " ").trim() || "";
			if (value) pieces.push(value);
		}
		const value = pieces.join(" ").trim();
		if (
			!value ||
			value.length > 120 ||
			hasPrivateQuickViewMarker(value) ||
			QUICK_VIEW_FORBIDDEN_PRICE_TEXT.test(value)
		)
			return null;
		return value;
	};

	const QUICK_VIEW_NAME_SELECTORS = [
		".product-title",
		".product-name",
		".card-title",
		"[itemprop='name']",
		".col-8 > a",
	];
	const readQuickViewName = (card) => {
		for (const selector of QUICK_VIEW_NAME_SELECTORS) {
			const value = visibleQuickViewText(card.querySelector(selector));
			if (value) return value;
		}
		return null;
	};

	const readQuickViewBrand = (card) => {
		for (const selector of [
			".brand-line",
			".product-brand",
			"[itemprop='brand']",
			"[data-brand]",
		]) {
			const value = visibleQuickViewText(card.querySelector(selector), 140);
			if (value) return value.replace(/^by\s+/i, "");
		}
		return null;
	};

	const readQuickViewAvailability = (card) => {
		const statuses = Array.from(
			card.querySelectorAll(
				".in-stock, .out-of-stock, .stock-status, .ks-stock-status, [data-availability]"
			)
		).filter((element) => visibleQuickViewText(element));
		const values = new Set(statuses.map((element) => visibleQuickViewText(element)));
		if (values.size === 1) return values.values().next().value;
		if (values.size > 1) return null;
		return QUICK_VIEW_DEFAULT_AVAILABILITY;
	};

	const readQuickViewCard = (card) => {
		if (
			!(card instanceof HTMLElement) ||
			!card.closest("#product-listing, .ks-home-product-shelf")
		)
			return null;
		const name = readQuickViewName(card);
		const href = readQuickViewLink(card);
		const image = name ? readQuickViewImage(card, name) : null;
		const price = readQuickViewPrice(card);
		const availability = readQuickViewAvailability(card);
		if (!name || !href || !image || !price || !availability) return null;
		const brand = readQuickViewBrand(card);
		if (
			brand === null &&
			card.querySelector(".brand-line, .product-brand, [itemprop='brand'], [data-brand]")
		)
			return null;
		return { availability, brand, href, image, name, price };
	};

	const createQuickViewElement = (tag, className, text) => {
		const element = document.createElement(tag);
		if (className) element.className = className;
		if (text) element.textContent = text;
		return element;
	};
	const createCardActionIcon = (kind) => {
		const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		svg.setAttribute("viewBox", "0 0 24 24");
		svg.setAttribute("aria-hidden", "true");
		svg.setAttribute("focusable", "false");
		svg.setAttribute("fill", "none");
		svg.setAttribute("stroke", "currentColor");
		svg.setAttribute("stroke-width", "2");
		svg.setAttribute("stroke-linecap", "round");
		svg.setAttribute("stroke-linejoin", "round");
		if (kind === "quick-view") {
			const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
			circle.setAttribute("cx", "11");
			circle.setAttribute("cy", "11");
			circle.setAttribute("r", "7");
			const handle = document.createElementNS("http://www.w3.org/2000/svg", "path");
			handle.setAttribute("d", "m20 20-4-4");
			svg.append(circle, handle);
		} else {
			const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
			path.setAttribute(
				"d",
				kind === "wishlist"
					? "M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21.2l7.8-7.8a5.5 5.5 0 0 0 0-7.8Z"
					: "m16 3 4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16"
			);
			svg.append(path);
		}
		return svg;
	};

	const arrangeCatalogueCardActions = (card) => {
		if (!(card instanceof HTMLElement) || !card.matches(".item-card")) return;
		const media = card.querySelector(".card-img-container");
		if (!(media instanceof HTMLElement)) return;
		const quickView = card.querySelector(`[${QUICK_VIEW_TRIGGER_ATTRIBUTE}]`);
		const wishlist = card.querySelector(".ks-wishlist-card-action");
		const comparison = card.querySelector(".ks-comparison-card-action");
		if (wishlist instanceof HTMLElement && !wishlist.querySelector("svg")) {
			wishlist.prepend(createCardActionIcon("wishlist"));
		}
		if (comparison instanceof HTMLElement && !comparison.querySelector("svg")) {
			comparison.prepend(createCardActionIcon("comparison"));
		}
		const controls = [quickView, wishlist, comparison].filter(
			(control) => control instanceof HTMLElement
		);
		let actions = media.querySelector(":scope > .ks-product-card-actions");
		if (!controls.length) {
			actions?.remove();
			return;
		}
		if (!(actions instanceof HTMLElement)) {
			actions = createQuickViewElement("div", "ks-product-card-actions");
			actions.setAttribute("role", "group");
			actions.setAttribute("aria-label", "Akcje produktu");
			media.append(actions);
		}
		controls.forEach((control, index) => {
			if (actions.children[index] !== control) actions.append(control);
		});
	};

	const createQuickViewDialog = (data) => {
		const id = ++quickViewSequence;
		const dialog = document.createElement("dialog");
		dialog.className = "ks-quick-view";
		dialog.setAttribute("role", "dialog");
		dialog.setAttribute("aria-modal", "true");
		dialog.setAttribute("aria-labelledby", `kuck-quick-view-title-${id}`);
		dialog.tabIndex = -1;
		const surface = createQuickViewElement("div", "ks-quick-view__surface");
		const close = createQuickViewElement("button", "ks-quick-view__close", "Zamknij");
		close.type = "button";
		close.setAttribute("aria-label", "Zamknij podgląd produktu");
		const heading = createQuickViewElement("h2", "ks-quick-view__title", data.name);
		heading.id = `kuck-quick-view-title-${id}`;
		const content = createQuickViewElement("div", "ks-quick-view__content");
		const image = createQuickViewElement("img", "ks-quick-view__image");
		image.src = data.image.src;
		image.alt = data.image.alt;
		content.append(image);
		const details = createQuickViewElement("div", "ks-quick-view__details");
		if (data.brand)
			details.append(createQuickViewElement("p", "ks-quick-view__brand", data.brand));
		details.append(heading);
		details.append(createQuickViewElement("p", "ks-quick-view__price", data.price));
		details.append(
			createQuickViewElement("p", "ks-quick-view__availability", data.availability)
		);
		const cta = createQuickViewElement("a", "ks-quick-view__cta", "Przejdź do produktu");
		cta.href = data.href;
		details.append(cta);
		content.append(details);
		surface.append(close, content);
		dialog.append(surface);
		return { close, dialog };
	};

	const quickViewFocusable = (dialog) =>
		Array.from(
			dialog.querySelectorAll(
				'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
			)
		).filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");

	const setQuickViewTriggersBusy = (listing, busy) => {
		listing.querySelectorAll(`[${QUICK_VIEW_TRIGGER_ATTRIBUTE}]`).forEach((element) => {
			if (!(element instanceof HTMLButtonElement)) return;
			if (busy) {
				if (element.disabled) return;
				element.disabled = true;
				element.setAttribute(QUICK_VIEW_BUSY_DISABLED_ATTRIBUTE, "true");
				return;
			}
			if (!element.hasAttribute(QUICK_VIEW_BUSY_DISABLED_ATTRIBUTE)) return;
			element.disabled = false;
			element.removeAttribute(QUICK_VIEW_BUSY_DISABLED_ATTRIBUTE);
		});
	};

	const openQuickView = (trigger) => {
		if (!(trigger instanceof HTMLButtonElement)) return;
		const listing = trigger.closest("#product-listing");
		if (
			!(listing instanceof HTMLElement) ||
			listing.getAttribute("aria-busy") === "true" ||
			trigger.disabled
		)
			return;
		const card = trigger.closest(".item-card, .list-row");
		const data = readQuickViewCard(card);
		if (!data) return;
		const { close, dialog } = createQuickViewDialog(data);
		const returnFocus = trigger;
		const previousScrollY = window.scrollY;
		let closed = false;
		const restoreFocus = () => {
			if (returnFocus instanceof HTMLElement && returnFocus.isConnected) {
				returnFocus.focus({ preventScroll: true });
				return;
			}
			if (card instanceof HTMLElement && card.isConnected) {
				card.querySelector("a[href]")?.focus({ preventScroll: true });
			}
		};
		const closeQuickView = () => {
			if (closed) return;
			closed = true;
			if (dialog.open && typeof dialog.close === "function") dialog.close();
			dialog.remove();
			document.body.classList.remove("ks-quick-view-open");
			if (window.scrollY !== previousScrollY && typeof window.scrollTo === "function") {
				window.scrollTo({ top: previousScrollY, left: window.scrollX, behavior: "auto" });
			}
			restoreFocus();
		};
		close.addEventListener("click", closeQuickView);
		dialog.addEventListener("click", (event) => {
			if (event.target === dialog) closeQuickView();
		});
		dialog.addEventListener("cancel", (event) => {
			event.preventDefault();
			closeQuickView();
		});
		dialog.addEventListener("keydown", (event) => {
			if (event.key === "Escape") {
				event.preventDefault();
				closeQuickView();
				return;
			}
			if (event.key !== "Tab") return;
			const items = quickViewFocusable(dialog);
			if (!items.length) {
				event.preventDefault();
				dialog.focus();
				return;
			}
			const first = items[0];
			const last = items[items.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		});
		document.body.append(dialog);
		document.body.classList.add("ks-quick-view-open");
		if (typeof dialog.showModal === "function") dialog.showModal();
		else dialog.setAttribute("open", "");
		close.focus({ preventScroll: true });
	};

	const initQuickViewCard = (card) => {
		if (!(card instanceof HTMLElement)) return;
		if (card.closest("#products-list-area")) return;
		const listing = card.closest("#product-listing, .ks-home-product-shelf");
		if (!(listing instanceof HTMLElement)) return;
		arrangeCatalogueCardActions(card);
		if (
			listing.getAttribute("aria-busy") === "true" ||
			card.hasAttribute(QUICK_VIEW_READY_ATTRIBUTE) ||
			card.querySelector(`[${QUICK_VIEW_TRIGGER_ATTRIBUTE}]`)
		)
			return;
		const data = readQuickViewCard(card);
		if (!data) return;
		const target = card.querySelector(".card-body, .col-10") || card;
		const trigger = createQuickViewElement("button", "ks-quick-view__trigger");
		trigger.type = "button";
		trigger.setAttribute(QUICK_VIEW_TRIGGER_ATTRIBUTE, "true");
		trigger.setAttribute("aria-label", `Szybki podgląd: ${data.name}`);
		trigger.append(createCardActionIcon("quick-view"));
		const label = createQuickViewElement("span", "sr-only", "Szybki podgląd");
		trigger.append(label);
		target.append(trigger);
		arrangeCatalogueCardActions(card);
		card.setAttribute(QUICK_VIEW_READY_ATTRIBUTE, "true");
	};

	const initQuickViewCards = (root = document) => {
		if (!isProductCardPage()) return;
		const cards = [];
		if (root instanceof HTMLElement) {
			const ownCard = root.matches(".item-card, .list-row")
				? root
				: root.closest(".item-card, .list-row");
			if (ownCard) cards.push(ownCard);
			root.querySelectorAll(".item-card, .list-row").forEach((card) => {
				if (
					card.closest("#product-listing, .ks-home-product-shelf") &&
					!cards.includes(card)
				)
					cards.push(card);
			});
		} else {
			root.querySelectorAll?.(
				"#product-listing .item-card, #product-listing .list-row, .ks-home-product-shelf .item-card"
			).forEach((card) => {
				if (!cards.includes(card)) cards.push(card);
			});
		}
		cards.forEach(initQuickViewCard);
	};

	const installProductImageHover = (root = document) => {
		if (!isProductCardPage()) return;
		const cards =
			root.querySelectorAll?.(
				"#product-listing .item-card, #product-listing .list-row, .ks-home-product-shelf .item-card"
			) || [];
		cards.forEach((card) => {
			if (!(card instanceof HTMLElement) || card.dataset.ksProductImageHover === "true")
				return;
			const image = card.querySelector(".card-img-container img, .list-image img");
			const link = card.querySelector(
				".card-img-container a[href], .product-title a[href], a[href]"
			);
			const route = link ? resolvePublicProductHref(link) : null;
			if (!(image instanceof HTMLImageElement) || !route) return;
			card.dataset.ksProductImageHover = "true";
			image.dataset.kuckPrimaryImage = image.currentSrc || image.src;
			image.dataset.kuckHoverRoute = route;
			const primarySrcset = image.getAttribute("srcset");
			let alternatePromise = null;
			let alternate = null;
			let hovered = false;

			const loadAlternate = () => {
				if (alternatePromise) return alternatePromise;
				alternatePromise = fetch(route, { credentials: "same-origin" })
					.then((response) => (response.ok ? response.text() : ""))
					.then((html) => {
						const productDocument = new DOMParser().parseFromString(html, "text/html");
						const second = productDocument.querySelectorAll(
							".ks-product-gallery__thumbnail img, .item-slideshow-image"
						)[1];
						const rawSource =
							second?.getAttribute("data-full-image") || second?.getAttribute("src");
						if (!rawSource) return null;
						const source = new URL(rawSource, route).href;
						if (source === image.dataset.kuckPrimaryImage) return null;
						const preload = new Image();
						preload.decoding = "async";
						preload.src = source;
						alternate = source;
						image.dataset.kuckHoverImage = source;
						return source;
					})
					.catch(() => null);
				return alternatePromise;
			};

			const swap = (showAlternate) => {
				if (showAlternate && !alternate) {
					loadAlternate().then((source) => {
						if (source && hovered) swap(true);
					});
					return;
				}
				const next = showAlternate ? alternate : image.dataset.kuckPrimaryImage;
				if (!next || next === image.src) return;
				window.clearTimeout(image.dataset.kuckHoverTimer);
				image.style.opacity = "0";
				image.dataset.kuckHoverTimer = window.setTimeout(() => {
					if (showAlternate) image.removeAttribute("srcset");
					else if (primarySrcset) image.setAttribute("srcset", primarySrcset);
					image.src = next;
					image.style.opacity = "1";
				}, 90);
			};

			card.addEventListener("pointerenter", (event) => {
				if (event.pointerType === "touch") return;
				hovered = true;
				swap(true);
			});
			card.addEventListener("pointerleave", () => {
				hovered = false;
				swap(false);
			});
			card.addEventListener("mouseenter", () => {
				hovered = true;
				swap(true);
			});
			card.addEventListener("mouseleave", () => {
				hovered = false;
				swap(false);
			});
			card.addEventListener("focusin", () => {
				hovered = true;
				swap(true);
			});
			card.addEventListener("focusout", (event) => {
				if (!card.contains(event.relatedTarget)) {
					hovered = false;
					swap(false);
				}
			});
		});
	};

	const installQuickViewSurface = (listing) => {
		if (!(listing instanceof HTMLElement) || listing.dataset.ksQuickViewInitialized === "true")
			return;
		listing.dataset.ksQuickViewInitialized = "true";
		listing.addEventListener("click", (event) => {
			const trigger = event.target.closest?.(`[${QUICK_VIEW_TRIGGER_ATTRIBUTE}]`);
			if (
				!(trigger instanceof HTMLButtonElement) ||
				!listing.contains(trigger) ||
				listing.getAttribute("aria-busy") === "true" ||
				trigger.disabled
			)
				return;
			event.preventDefault();
			event.stopPropagation();
			openQuickView(trigger);
		});
		listing.addEventListener("keydown", (event) => {
			const trigger = event.target.closest?.(`[${QUICK_VIEW_TRIGGER_ATTRIBUTE}]`);
			if (!(trigger instanceof HTMLButtonElement) || !listing.contains(trigger)) return;
			if (event.key !== "Enter" && event.key !== " ") return;
			if (listing.getAttribute("aria-busy") === "true" || trigger.disabled) return;
			event.preventDefault();
			trigger.click();
		});
		let quickViewListingReady = listing.getAttribute("aria-busy") !== "true";
		setQuickViewTriggersBusy(listing, !quickViewListingReady);
		const quickViewVisibilityObserver = new MutationObserver((mutations) => {
			const touchedCards = new Set();
			let listingBecameReady = false;
			mutations.forEach((mutation) => {
				if (mutation.target === listing && mutation.attributeName === "aria-busy") {
					const listingIsBusy = listing.getAttribute("aria-busy") === "true";
					if (listingIsBusy) {
						quickViewListingReady = false;
						setQuickViewTriggersBusy(listing, true);
					} else if (!quickViewListingReady) {
						quickViewListingReady = true;
						listingBecameReady = true;
					}
					return;
				}
				const card =
					mutation.target instanceof Element
						? mutation.target.closest(".item-card, .list-row")
						: null;
				if (
					card instanceof HTMLElement &&
					card.closest("#product-listing, .ks-home-product-shelf") === listing
				)
					touchedCards.add(card);
			});
			if (listingBecameReady) {
				setQuickViewTriggersBusy(listing, false);
				initQuickViewCards(listing);
				return;
			}
			touchedCards.forEach(initQuickViewCard);
		});
		quickViewVisibilityObserver.observe(listing, {
			attributes: true,
			attributeFilter: QUICK_VIEW_VISIBILITY_ATTRIBUTE_FILTER,
			subtree: true,
		});
		initQuickViewCards(listing);
	};

	const installCatalogueQuickView = () => {
		if (!isProductCardPage()) return;
		document
			.querySelectorAll("#product-listing, .ks-home-product-shelf")
			.forEach((listing) => {
				installQuickViewSurface(listing);
				installProductImageHover(listing);
			});
	};

	window.kuckQuickView = Object.freeze({ refresh: installCatalogueQuickView });

	improveHomeCardLinks();
	removeInactiveQuoteLinks();
	labelAccessibleLinks();
	repairCatalogueSearchDescription();
	syncCatalogueSearchValue();
	removeGuestSyntheticCatalogueMarkers();
	normalizeProductDescriptionSeparators();
	initProductDescriptionToggles();
	installCatalogueQuickView();
	new MutationObserver((mutations) => {
		mutations.forEach((mutation) =>
			mutation.addedNodes.forEach((node) => {
				if (node instanceof Element) {
					improveHomeCardLinks(node);
					removeInactiveQuoteLinks(node);
					labelAccessibleLinks(node);
					repairCatalogueSearchDescription(node);
					syncCatalogueSearchValue(node);
					removeGuestSyntheticCatalogueMarkers(node);
					initProductDescriptionToggles(node);
					installCatalogueQuickView();
					initQuickViewCards(node);
					installProductImageHover(node);
				}
			})
		);
		normalizeProductDescriptionSeparators();
	}).observe(document.body, { childList: true, subtree: true });

	const navbarToggle = document.querySelector("[data-ks-navbar-toggle]");
	const navbarPanel = navbarToggle
		? document.getElementById(navbarToggle.getAttribute("aria-controls"))
		: null;
	const setNavbarOpen = (open) => {
		if (!navbarToggle || !navbarPanel) return;
		navbarPanel.classList.toggle("show", open);
		navbarToggle.setAttribute("aria-expanded", String(open));
		navbarToggle.setAttribute("aria-label", open ? "Zamknij menu" : "Otwórz menu");
	};
	navbarToggle?.addEventListener("click", () => {
		setNavbarOpen(!navbarPanel?.classList.contains("show"));
	});
	navbarToggle?.addEventListener("keydown", (event) => {
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		navbarToggle.click();
	});
	document.addEventListener("keydown", (event) => {
		if (event.key !== "Escape" || !navbarPanel?.classList.contains("show")) return;
		setNavbarOpen(false);
		navbarToggle?.focus();
	});

	const megaItems = Array.from(document.querySelectorAll("[data-ks-mega-item]"));
	const megaDesktopMedia = window.matchMedia?.("(min-width: 1024px)") || null;
	let openMegaItem = null;
	let megaCloseTimer = null;
	const megaPanelTimers = new WeakMap();
	let focusRestoreItem = null;

	const isMegaDesktop = () => Boolean(megaDesktopMedia?.matches);
	const setMegaExpanded = (item, open) => {
		const trigger = item.querySelector("[data-ks-mega-trigger]");
		const toggle = item.querySelector("[data-ks-mega-toggle]");
		const panel = item.querySelector("[data-ks-mega-panel]");
		if (!panel) return;
		window.clearTimeout(megaPanelTimers.get(panel));
		panel.classList.remove("is-opening", "is-closing");
		item.classList.toggle("is-open", open);
		if (open) {
			panel.hidden = false;
			panel.classList.add("is-opening");
			window.requestAnimationFrame(() => {
				if (item.classList.contains("is-open")) panel.classList.remove("is-opening");
			});
		} else if (!panel.hidden) {
			panel.classList.add("is-closing");
			megaPanelTimers.set(
				panel,
				window.setTimeout(
					() => {
						if (!item.classList.contains("is-open")) {
							panel.hidden = true;
							panel.classList.remove("is-closing");
						}
					},
					window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? 0 : 220
				)
			);
		}
		trigger?.setAttribute("aria-expanded", String(open));
		toggle?.setAttribute("aria-expanded", String(open));
		if (toggle && trigger) {
			const label = trigger.textContent?.trim() || "menu";
			toggle.setAttribute("aria-label", `${open ? "Ukryj" : "Pokaż"} menu ${label}`);
		}
	};

	const closeMega = (item, restoreFocus = false) => {
		if (!item) return;
		const trigger = item.querySelector("[data-ks-mega-trigger]");
		setMegaExpanded(item, false);
		if (openMegaItem === item) openMegaItem = null;
		if (restoreFocus && trigger instanceof HTMLElement) {
			focusRestoreItem = item;
			trigger.focus();
		}
	};

	const openMega = (item) => {
		window.clearTimeout(megaCloseTimer);
		megaItems.forEach((other) => {
			if (other !== item) closeMega(other);
		});
		setMegaExpanded(item, true);
		openMegaItem = item;
	};

	const scheduleMegaClose = (item) => {
		window.clearTimeout(megaCloseTimer);
		megaCloseTimer = window.setTimeout(() => {
			if (!item.contains(document.activeElement)) closeMega(item);
		}, 120);
	};

	megaItems.forEach((item) => {
		const trigger = item.querySelector("[data-ks-mega-trigger]");
		const toggle = item.querySelector("[data-ks-mega-toggle]");
		const panel = item.querySelector("[data-ks-mega-panel]");
		if (!trigger || !toggle || !panel) return;
		setMegaExpanded(item, false);

		toggle.addEventListener("click", (event) => {
			event.preventDefault();
			event.stopPropagation();
			if (openMegaItem === item && !panel.hidden) closeMega(item);
			else openMega(item);
		});
		item.addEventListener("pointerenter", () => {
			if (isMegaDesktop()) openMega(item);
		});
		item.addEventListener("pointerleave", () => {
			if (isMegaDesktop()) scheduleMegaClose(item);
		});
		item.addEventListener("focusin", () => {
			if (focusRestoreItem === item) {
				focusRestoreItem = null;
				return;
			}
			if (isMegaDesktop()) openMega(item);
		});
		item.addEventListener("focusout", (event) => {
			if (isMegaDesktop() && !item.contains(event.relatedTarget)) scheduleMegaClose(item);
		});
	});

	document.addEventListener("pointerdown", (event) => {
		if (openMegaItem && !openMegaItem.contains(event.target)) closeMega(openMegaItem);
	});
	// Capture first so Escape closes the nested panel before the outer mobile
	// collapse, keeping focus on a visible, meaningful trigger.
	document.addEventListener(
		"keydown",
		(event) => {
			if (event.key !== "Escape" || !openMegaItem) return;
			const item = openMegaItem;
			closeMega(item, true);
			event.stopPropagation();
		},
		true
	);
	megaDesktopMedia?.addEventListener?.("change", () => {
		window.clearTimeout(megaCloseTimer);
		megaItems.forEach((item) => closeMega(item));
	});
	navbarToggle?.addEventListener("click", () => {
		if (!navbarPanel?.classList.contains("show")) megaItems.forEach((item) => closeMega(item));
	});

	const localizeStandardWebshop = () => {
		const productSearch = document.querySelector(
			'#product-listing input[placeholder="Search for Products"]'
		);
		if (productSearch) productSearch.placeholder = "Szukaj produktów";
		const stockTranslations = {
			"Available on backorder": "Dostępny na zamówienie",
			"Out of stock": "Niedostępny online",
			"In stock": "Dostępny online",
		};
		document
			.querySelectorAll("#product-listing .out-of-stock, #product-listing .in-stock")
			.forEach((element) => {
				const translated = stockTranslations[element.textContent?.trim()];
				if (translated) {
					element.textContent = translated;
					element.style.removeProperty("color");
				}
			});
		const buttonTranslations = { Prev: "Poprzednia", Next: "Następna" };
		document.querySelectorAll(".product-paging-area button").forEach((button) => {
			const translated = buttonTranslations[button.textContent?.trim()];
			if (translated) button.textContent = translated;
		});
		const searchTranslations = {
			Recent: "Ostatnie wyszukiwania",
			"No searches yet.": "Brak ostatnich wyszukiwań.",
			Categories: "Kategorie",
		};
		document
			.querySelectorAll(
				"#search-results-container b, #search-results-container #recents span"
			)
			.forEach((element) => {
				const translated = searchTranslations[element.textContent?.trim()];
				if (translated) element.textContent = translated;
			});
		document.querySelectorAll("#search-results-container .brand-line").forEach((element) => {
			const value = element.textContent?.trim() || "";
			if (value.startsWith("by ")) element.textContent = `marka: ${value.slice(3)}`;
		});
		document.querySelectorAll("#product-listing .product-info-green").forEach((element) => {
			const value = element.textContent?.trim() || "";
			if (value.endsWith(" OFF")) element.textContent = `${value.slice(0, -4)} taniej`;
		});
		removeGuestSyntheticCatalogueMarkers(document);
	};
	localizeStandardWebshop();
	const initProductTabs = () => {
		const nav = document.querySelector(".ks-product-section-nav");
		const description = document.querySelector("#kuck-product-description");
		const salons = document.querySelector("#kuck-product-salons");
		const details = document.querySelector("#kuck-product-details");
		const reviews = document.querySelector("#kuck-product-reviews");
		if (!nav || !description) return;
		const trust = document.querySelector(".ks-product-trust-strip");
		const secondary = nav.closest(".ks-webshop-product__secondary");
		if (trust && secondary && trust.parentElement !== secondary)
			secondary.insertBefore(trust, nav);
		const panelHost = document.querySelector(
			".ks-webshop-product__secondary .product-page-content"
		);
		if (panelHost) {
			if (salons && salons.parentElement !== panelHost) panelHost.prepend(salons);
			if (description.parentElement !== panelHost) panelHost.append(description);
		}
		const panels = [description, salons, details, reviews].filter(Boolean);
		const links = [...nav.querySelectorAll(".ks-product-section-nav__link")];
		links.forEach((link) => {
			const targetId = (link.getAttribute("href") || "").slice(1);
			const panel = document.getElementById(targetId);
			if (!panel) return;
			const button = document.createElement("button");
			button.type = "button";
			button.className = link.className;
			button.textContent = link.textContent;
			button.setAttribute("aria-controls", targetId);
			button.setAttribute("aria-selected", "false");
			link.replaceWith(button);
			button.addEventListener("click", () => {
				nav.querySelectorAll("button").forEach((tab) =>
					tab.setAttribute("aria-selected", String(tab === button))
				);
				panels.forEach((section) => {
					section.hidden = section !== panel;
				});
			});
		});
		const first = nav.querySelector("button");
		if (first) first.click();
	};
	initProductTabs();
	new MutationObserver(localizeStandardWebshop).observe(document.body, {
		childList: true,
		subtree: true,
	});

	const addToCartButton = document.querySelector("[data-kuck-add-to-cart]");
	const cartStatus = document.querySelector("[data-kuck-cart-status]");
	const GUEST_CART_METHOD = "kuck_shop.checkout.frappe_guest_cart.update_guest_cart";
	const callCartMethod = async (method, args) => {
		const metaToken = document.querySelector('meta[name="csrf-token"]')?.content?.trim();
		const frappeToken =
			typeof window.frappe?.csrf_token === "string" ? window.frappe.csrf_token.trim() : "";
		const validToken = (token) =>
			typeof token === "string" &&
			token.length > 0 &&
			token.toLowerCase() !== "none" &&
			!/[\u0000-\u001f\u007f]/u.test(token);
		const csrfToken = [metaToken, frappeToken].find(validToken) || "";
		if (!csrfToken) throw new Error("CSRF_UNAVAILABLE");
		const guestMethod = method === GUEST_CART_METHOD;
		const body = guestMethod ? JSON.stringify(args) : new URLSearchParams();
		if (!guestMethod)
			Object.entries(args).forEach(([name, value]) => body.set(name, String(value)));
		const response = await window.fetch(`/api/method/${method}`, {
			method: "POST",
			credentials: "same-origin",
			headers: {
				"Content-Type": guestMethod
					? "application/json;charset=UTF-8"
					: "application/x-www-form-urlencoded;charset=UTF-8",
				"X-Frappe-CSRF-Token": csrfToken,
			},
			body,
		});
		if (!response.ok) throw new Error("REQUEST_REJECTED");
		const result = await response.json();
		const count = result?.message?.item_count;
		if (method.endsWith("update_cart") || method.endsWith("update_guest_cart"))
			window.kuckCartNav?.updated?.(count);
		return result;
	};
	const updateGuestCart = (itemCode, quantity, expectedRevision) => {
		const args = { item_code: itemCode, qty: quantity };
		if (expectedRevision) args.expected_revision = expectedRevision;
		return callCartMethod(GUEST_CART_METHOD, args);
	};
	// Listing/home enhancements run in a later bundle section. Expose only the
	// narrow app-owned operation; the generic transport and arbitrary methods
	// remain private to this cart boundary.
	window.kuckGuestCart = Object.freeze({
		update: updateGuestCart,
		// Read only the already-public PDP through the existing same-origin gate.
		resolveProduct: (element) => resolveGuestCatalogueItemCode(element),
	});
	const couponForm = document.querySelector("[data-kuck-cart-coupon]");
	if (couponForm) {
		const input = couponForm.querySelector('input[name="coupon_code"]');
		const status = couponForm.querySelector("[data-kuck-coupon-status]");
		const controls = Array.from(couponForm.querySelectorAll("input, button"));
		const updateCoupon = async (code, revision) => {
			if (!/^[0-9a-f]{64}$/.test(revision || "")) {
				if (status) status.textContent = "Odśwież koszyk i spróbuj ponownie.";
				return;
			}
			controls.forEach((control) => {
				control.disabled = true;
			});
			if (status) {
				status.setAttribute("role", "status");
				status.textContent = code ? "Sprawdzamy kod rabatowy…" : "Usuwamy kod rabatowy…";
			}
			try {
				const response = await callCartMethod(
					"kuck_shop.checkout.frappe_order.update_coupon_code",
					{ coupon_code: code, expected_cart_revision: revision }
				);
				if (
					response?.message?.code !== "READY" ||
					!/^[0-9a-f]{64}$/.test(response?.message?.cart_revision || "")
				) {
					throw new Error("COUPON_NOT_UPDATED");
				}
				window.location.reload();
			} catch (_error) {
				controls.forEach((control) => {
					control.disabled = false;
				});
				if (status) {
					status.setAttribute("role", "alert");
					status.textContent = code
						? "Kod jest nieprawidłowy, wygasł lub nie obejmuje produktów w koszyku."
						: "Nie udało się usunąć kodu. Odśwież koszyk i spróbuj ponownie.";
				}
			}
		};
		couponForm.addEventListener("submit", (event) => {
			event.preventDefault();
			if (!couponForm.reportValidity()) return;
			const revision =
				couponForm.querySelector("button[type='submit']")?.dataset.cartRevision;
			updateCoupon(input?.value.trim() || "", revision);
		});
		couponForm
			.querySelector("[data-kuck-remove-coupon]")
			?.addEventListener("click", (event) => {
				updateCoupon("", event.currentTarget.dataset.cartRevision);
			});
	}
	const isGuestSession = () => {
		const status = document.body?.getAttribute("frappe-session-status");
		if (status === "logged-out") return true;
		if (status === "logged-in") return false;
		return !window.frappe?.session?.user || window.frappe.session.user === "Guest";
	};
	const focusCartIntent = () => {
		if (window.location.hash !== "#kuck-add-to-cart") return;
		window.setTimeout(() => {
			if (!addToCartButton) return;
			addToCartButton.focus({ preventScroll: false });
			if (cartStatus)
				cartStatus.textContent = "Kliknij ponownie, aby dodać produkt do koszyka.";
		}, 0);
	};
	addToCartButton?.addEventListener("click", async () => {
		const itemCode = addToCartButton.dataset.itemCode;
		if (!itemCode || (!isGuestSession() && !window.frappe?.call)) return;
		addToCartButton.disabled = true;
		if (cartStatus) cartStatus.textContent = "Dodajemy produkt do koszyka…";
		try {
			const response = isGuestSession()
				? await updateGuestCart(itemCode, 1)
				: await window.frappe.call({
						type: "POST",
						method: "webshop.webshop.shopping_cart.cart.update_cart",
						args: { item_code: itemCode, qty: 1 },
				  });
			if (!response?.message?.name && response?.message?.code !== "READY") {
				throw new Error("CART_NOT_UPDATED");
			}
			window.kuckCartNav?.refresh();
			window.location.assign("/koszyk");
		} catch (_error) {
			addToCartButton.disabled = false;
			if (cartStatus) {
				cartStatus.setAttribute("role", "alert");
				cartStatus.textContent = "Nie udało się dodać produktu. Spróbuj ponownie.";
			}
		}
	});
	focusCartIntent();

	document.querySelectorAll("[data-kuck-guest-cart-line]").forEach((form) => {
		const status = form.querySelector("[data-kuck-guest-line-status]");
		const quantity = form.querySelector('input[name="quantity"]');
		const submit = form.querySelector('button[type="submit"]');
		const remove = form.querySelector("[data-kuck-remove-guest-line]");
		const updateLine = async (nextQuantity) => {
			const itemCode = form.dataset.itemCode;
			const revision = form.dataset.cartRevision;
			if (!itemCode || !revision) return;
			if (submit) submit.disabled = true;
			if (remove) remove.disabled = true;
			if (status)
				status.textContent =
					nextQuantity === 0 ? "Usuwamy produkt…" : "Aktualizujemy ilość…";
			try {
				const response = await callCartMethod(GUEST_CART_METHOD, {
					item_code: itemCode,
					qty: nextQuantity,
					expected_revision: revision,
				});
				if (!["READY", "EMPTY"].includes(response?.message?.code)) {
					throw new Error("CART_NOT_UPDATED");
				}
				window.kuckCartNav?.refresh();
				window.location.reload();
			} catch (_error) {
				if (submit) submit.disabled = false;
				if (remove) remove.disabled = false;
				if (status) {
					status.setAttribute("role", "alert");
					status.textContent =
						"Nie udało się zmienić koszyka. Odśwież stronę i spróbuj ponownie.";
				}
			}
		};
		form.addEventListener("submit", (event) => {
			event.preventDefault();
			if (!form.reportValidity()) return;
			updateLine(Number(quantity?.value));
		});
		remove?.addEventListener("click", () => updateLine(0));
	});

	document.querySelectorAll("[data-kuck-remove-cart-line]").forEach((button) => {
		button.addEventListener("click", async () => {
			const itemCode = button.dataset.itemCode;
			if (!itemCode) return;
			button.disabled = true;
			button.setAttribute("aria-busy", "true");
			try {
				const response = await callCartMethod(
					"webshop.webshop.shopping_cart.cart.update_cart",
					{ item_code: itemCode, qty: 0 }
				);
				const removedCartName = response?.message?.name;
				if (
					!response?.message ||
					!(
						removedCartName === null ||
						(typeof removedCartName === "string" && removedCartName.length > 0)
					)
				) {
					throw new Error("CART_NOT_UPDATED");
				}
				window.kuckCartNav?.refresh();
				window.location.reload();
			} catch (_error) {
				button.disabled = false;
				button.removeAttribute("aria-busy");
				button.title = "Nie udało się usunąć produktu. Spróbuj ponownie.";
			}
		});
	});

	const safeGuestCatalogueRoute = (value) => {
		if (typeof value !== "string" || !value.trim()) return null;
		try {
			const url = new URL(value, window.location.origin);
			if (url.origin !== window.location.origin || url.search || url.hash) return null;
			const path = url.pathname.replace(/^\/+/, "");
			if (!path || path.split("/").some((part) => !part || part === "." || part === ".."))
				return null;
			if (
				/^(?:api|app|desk|login|logout|private|konto|account|cart|koszyk)(?:\/|$)/i.test(
					path
				)
			)
				return null;
			return `/${path}`;
		} catch (_error) {
			return null;
		}
	};
	const guestCatalogueRoute = (element) => {
		const declaredRoute = element.dataset?.kuckProductRoute;
		if (declaredRoute) return safeGuestCatalogueRoute(declaredRoute);
		const link = element
			.closest(".item-card, .list-row, .ks-home-product-shelf .item-card")
			?.querySelector("a[href]");
		if (!(link instanceof HTMLAnchorElement)) return null;
		return safeGuestCatalogueRoute(link.getAttribute("href") || "");
	};
	const resolveGuestCatalogueItemCode = async (element) => {
		const route = guestCatalogueRoute(element);
		if (!route) return null;
		const response = await window.fetch(route, {
			mode: "same-origin",
			credentials: "same-origin",
			cache: "no-store",
			redirect: "follow",
			headers: { Accept: "text/html" },
		});
		if (!response?.ok) return null;
		const finalUrl = new URL(response.url || route, window.location.origin);
		if (
			finalUrl.origin !== window.location.origin ||
			finalUrl.search ||
			finalUrl.hash ||
			!safeGuestCatalogueRoute(finalUrl.pathname)
		)
			return null;
		const contentType = response.headers?.get?.("content-type") || "";
		if (contentType && !/text\/html/i.test(contentType)) return null;
		const markup = await response.text();
		if (typeof markup !== "string" || !markup) return null;
		const parsed = new DOMParser().parseFromString(markup, "text/html");
		const addButton = parsed.querySelector(
			"[data-kuck-add-to-cart][data-item-code], [data-kr-pdp-add][data-item-code]"
		);
		if (!addButton || addButton.hasAttribute("disabled")) return null;
		const itemCode = (addButton.getAttribute("data-item-code") || "").trim();
		return /^[^\u0000-\u001f\u007f]{1,140}$/u.test(itemCode) ? itemCode : null;
	};
	const guestCatalogueStatus = (button) => {
		const container = button.closest(
			".primary-action-section, .cart-action-container, .card-body, .col-10"
		);
		if (!container) return null;
		let status = container.querySelector("[data-kuck-guest-catalogue-status]");
		if (!status) {
			status = document.createElement("span");
			status.className = "ks-cart-action-status";
			status.dataset.kuckGuestCatalogueStatus = "";
			status.setAttribute("aria-live", "polite");
			container.append(status);
		}
		return status;
	};
	const setGuestCatalogueStatus = (button, message, error = false) => {
		const status = guestCatalogueStatus(button);
		if (!status) return;
		status.textContent = message;
		status.setAttribute("role", error ? "alert" : "status");
	};
	if (document.body && document.body.dataset.ksGuestCatalogueCartBound !== "true") {
		document.body.dataset.ksGuestCatalogueCartBound = "true";
		document.addEventListener(
			"click",
			async (event) => {
				if (!isGuestSession()) return;
				const source = event.target;
				const button = source?.closest?.(
					"#product-listing .btn-add-to-cart-list:not(.go-to-cart):not(.go-to-cart-grid), .ks-home-product-shelf .btn-add-to-cart-list:not(.go-to-cart):not(.go-to-cart-grid)"
				);
				if (!(button instanceof HTMLElement)) return;
				event.preventDefault();
				event.stopImmediatePropagation();
				if (button.disabled) return;
				button.disabled = true;
				try {
					const itemCode =
						button.dataset.itemCode?.trim() ||
						(await resolveGuestCatalogueItemCode(button));
					if (!itemCode) throw new Error("PRODUCT_CART_ACTION_UNAVAILABLE");
					const response = await updateGuestCart(itemCode, 1);
					if (response?.message?.code !== "READY") throw new Error("CART_NOT_UPDATED");
					window.dispatchEvent(
						new CustomEvent("kuck:cart-updated", {
							detail: { count: response.message.item_count },
						})
					);
				} catch (_error) {
					setGuestCatalogueStatus(
						button,
						"Nie udało się dodać produktu. Spróbuj ponownie.",
						true
					);
				} finally {
					button.disabled = false;
				}
			},
			true
		);
	}

	const placeOrderButton = document.querySelector("[data-kuck-place-order]");
	const orderStatus = document.querySelector("[data-kuck-order-status]");
	const deliveryBox = document.querySelector("[data-kuck-delivery]");
	const deliveryMethod = () =>
		document.querySelector('input[name="checkout-delivery-method"]:checked')?.value || null;
	const updateDeliverySummary = () => {
		if (!deliveryBox) return;
		const payment = document.querySelector(
			'input[name="guest_payment_method"]:checked, input[name="checkout-payment-method"]:checked'
		)?.value;
		const fee = Number(
			payment === "COD"
				? deliveryBox.dataset.codFeeMinor
				: deliveryBox.dataset.prepaidFeeMinor
		);
		const merchandise = Number(deliveryBox.dataset.merchandiseMinor);
		if (
			!Number.isSafeInteger(fee) ||
			!Number.isSafeInteger(merchandise) ||
			fee < 0 ||
			merchandise < 0
		)
			return;
		const format = (minor) =>
			new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" })
				.format(minor / 100)
				.replace(/,00(?=\s|$)/, "");
		document
			.querySelectorAll("[data-kuck-delivery-total], [data-kuck-delivery-price]")
			.forEach((node) => {
				node.textContent = format(fee);
			});
		const total = document.querySelector("[data-kuck-checkout-total]");
		if (total) total.textContent = format(merchandise + fee);
	};
	document
		.querySelectorAll(
			'input[name="guest_payment_method"], input[name="checkout-payment-method"]'
		)
		.forEach((input) => input.addEventListener("change", updateDeliverySummary));
	updateDeliverySummary();
	const tpayRedirect = (value, fields) => {
		const target = new URL(value);
		if (
			target.protocol !== "https:" ||
			target.hostname !== "secure.tpay.com" ||
			target.username ||
			target.password ||
			target.port
		) {
			throw new Error("INVALID_PAYMENT_REDIRECT");
		}
		const entries = Object.entries(fields || {});
		const allowed = new Set([
			"id",
			"amount",
			"description",
			"crc",
			"online",
			"result_url",
			"return_url",
			"return_error_url",
			"language",
			"email",
			"name",
			"accept_tos",
			"module",
			"md5sum",
		]);
		if (entries.some(([key, item]) => !allowed.has(key) || typeof item !== "string")) {
			throw new Error("INVALID_PAYMENT_FORM");
		}
		if (!entries.length) {
			window.location.assign(target.href);
			return;
		}
		const form = document.createElement("form");
		form.method = "post";
		form.action = target.href;
		for (const [key, item] of entries) {
			const input = document.createElement("input");
			input.type = "hidden";
			input.name = key;
			input.value = item;
			form.appendChild(input);
		}
		document.body.appendChild(form);
		form.submit();
	};
	const checkoutKey = (revision) => `kuck.checkout.v1.${revision}`;
	const idempotencyKey = (revision) => {
		const storageKey = checkoutKey(revision);
		let value = window.sessionStorage?.getItem(storageKey);
		if (!value) {
			value = `browser-${window.crypto.randomUUID()}`;
			window.sessionStorage?.setItem(storageKey, value);
		}
		return value;
	};
	placeOrderButton?.addEventListener("click", async () => {
		const revision = placeOrderButton.dataset.cartRevision;
		const billingAddress = document.querySelector(
			'input[name="checkout-billing-address"]:checked'
		)?.value;
		const shippingAddress = document.querySelector(
			'input[name="checkout-shipping-address"]:checked'
		)?.value;
		const paymentMethod = document.querySelector(
			'input[name="checkout-payment-method"]:checked'
		)?.value;
		if (!revision || !billingAddress || !shippingAddress || !paymentMethod) return;
		placeOrderButton.disabled = true;
		if (orderStatus) orderStatus.textContent = "Tworzymy zamówienie…";
		try {
			const billingResponse = await callCartMethod(
				"webshop.webshop.shopping_cart.cart.update_cart_address",
				{ address_type: "billing", address_name: billingAddress }
			);
			const billingRevision = billingResponse?.message?.cart_revision;
			if (!billingRevision) throw new Error("CART_BILLING_NOT_UPDATED");
			const shippingResponse = await callCartMethod(
				"webshop.webshop.shopping_cart.cart.update_cart_address",
				{ address_type: "shipping", address_name: shippingAddress }
			);
			const finalCartRevision = shippingResponse?.message?.cart_revision;
			if (!finalCartRevision) throw new Error("CART_SHIPPING_NOT_UPDATED");
			const response = await callCartMethod(
				"webshop.webshop.shopping_cart.cart.place_order",
				{
					expected_cart_revision: finalCartRevision,
					idempotency_key: idempotencyKey(finalCartRevision),
					payment_method: paymentMethod,
					delivery_method: deliveryMethod(),
				}
			);
			const salesOrder = response?.message?.sales_order;
			const checkoutAttempt = response?.message?.attempt;
			if (!salesOrder || !checkoutAttempt) throw new Error("ORDER_NOT_CREATED");
			if (paymentMethod === "COD") {
				if (orderStatus) orderStatus.textContent = "Potwierdzamy płatność przy odbiorze…";
				const codResponse = await callCartMethod(
					"kuck_shop.checkout.frappe_cod.confirm_cod_order",
					{ checkout_public_id: checkoutAttempt }
				);
				const redirectUrl = codResponse?.message?.redirect_url;
				if (!redirectUrl) throw new Error("COD_NOT_CONFIRMED");
				window.sessionStorage?.removeItem(checkoutKey(finalCartRevision));
				window.location.assign(redirectUrl);
				return;
			}
			if (orderStatus) orderStatus.textContent = "Przechodzimy do bezpiecznej płatności…";
			const paymentResponse = await callCartMethod(
				"kuck_shop.payments.frappe_checkout.start_payment",
				{
					checkout_public_id: checkoutAttempt,
					idempotency_key: `tpay-${idempotencyKey(finalCartRevision)}`,
				}
			);
			const redirectUrl = paymentResponse?.message?.redirect_url;
			if (!redirectUrl) throw new Error("PAYMENT_NOT_STARTED");
			window.sessionStorage?.removeItem(checkoutKey(finalCartRevision));
			tpayRedirect(redirectUrl, paymentResponse?.message?.form_fields);
		} catch (_error) {
			placeOrderButton.disabled = false;
			if (orderStatus) {
				orderStatus.setAttribute("role", "alert");
				orderStatus.textContent = "Nie udało się utworzyć zamówienia. Spróbuj ponownie.";
			}
		}
	});

	const guestCheckoutForm = document.querySelector("[data-kuck-guest-checkout]");
	const guestOrderStatus = document.querySelector("[data-kuck-guest-order-status]");
	const guestConsentFields = Array.from(
		guestCheckoutForm?.querySelectorAll(
			'input[type="checkbox"][name="terms"], input[type="checkbox"][name="privacy"]'
		) || []
	).map((input) => ({
		input,
		error: input.closest("label")?.querySelector("[data-kuck-consent-error]"),
	}));
	const setGuestConsentError = ({ input, error }, visible) => {
		if (!input || !error) return;
		if (visible) {
			input.setAttribute("aria-invalid", "true");
			input.setAttribute("aria-describedby", error.id);
		} else {
			input.removeAttribute("aria-invalid");
			input.removeAttribute("aria-describedby");
		}
		error.hidden = !visible;
	};
	guestConsentFields.forEach((field) =>
		field.input.addEventListener("change", () => {
			if (field.input.checked) setGuestConsentError(field, false);
		})
	);
	let guestPendingPayment = null;
	guestCheckoutForm?.addEventListener("submit", async (event) => {
		event.preventDefault();
		const missingConsents = guestConsentFields.filter(({ input }) => !input.checked);
		guestConsentFields.forEach((field) => setGuestConsentError(field, !field.input.checked));
		if (missingConsents.length) {
			missingConsents[0].input.focus();
			return;
		}
		if (!guestCheckoutForm.reportValidity()) return;
		const submit = guestCheckoutForm.querySelector('button[type="submit"]');
		const revision = submit?.dataset.cartRevision;
		const data = new FormData(guestCheckoutForm);
		const paymentMethod = guestPendingPayment
			? "TPAY_HOSTED"
			: data.get("guest_payment_method");
		if (!revision || !paymentMethod) return;
		submit.disabled = true;
		if (guestOrderStatus) guestOrderStatus.textContent = "Bezpiecznie zapisujemy zamówienie…";
		try {
			const key = guestPendingPayment?.key || idempotencyKey(`guest-${revision}`);
			const response = guestPendingPayment
				? { message: guestPendingPayment }
				: await callCartMethod(
						"kuck_shop.checkout.frappe_guest_checkout.place_guest_order",
						{
							expected_cart_revision: revision,
							idempotency_key: key,
							full_name: data.get("full_name"),
							email: data.get("email"),
							phone: data.get("phone"),
							street: data.get("street"),
							postal_code: data.get("postal_code"),
							city: data.get("city"),
							terms_accepted: data.get("terms") === "on",
							privacy_accepted: data.get("privacy") === "on",
							payment_method: paymentMethod,
							delivery_method: deliveryMethod(),
						}
				  );
			const order = response?.message?.sales_order;
			if (!order) throw new Error("ORDER_NOT_CREATED");
			if (paymentMethod === "TPAY_HOSTED" || guestPendingPayment) {
				const attempt = response?.message?.attempt;
				if (!attempt) throw new Error("ATTEMPT_NOT_CREATED");
				guestPendingPayment = { key, attempt, sales_order: order };
				guestCheckoutForm.querySelectorAll("input").forEach((input) => {
					input.disabled = true;
				});
				if (guestOrderStatus)
					guestOrderStatus.textContent =
						"Zamówienie zapisane. Przechodzimy do płatności Tpay…";
				const payment = await callCartMethod(
					"kuck_shop.payments.frappe_guest_checkout.start_guest_payment",
					{ checkout_public_id: attempt, idempotency_key: `tpay-${key}` }
				);
				tpayRedirect(payment?.message?.redirect_url, payment?.message?.form_fields);
				return;
			}
			window.sessionStorage?.removeItem(checkoutKey(`guest-${revision}`));
			const heading = document.createElement("h2");
			heading.textContent = "Zamówienie przyjęte";
			const confirmation = document.createElement("p");
			confirmation.setAttribute("role", "status");
			confirmation.append("Numer zamówienia: ");
			const reference = document.createElement("strong");
			reference.textContent = order;
			confirmation.append(
				reference,
				". Zapisaliśmy dane potrzebne do realizacji zamówienia."
			);
			const returnLink = document.createElement("a");
			returnLink.href = "/all-products";
			returnLink.textContent = "Wróć do produktów";
			guestCheckoutForm.replaceChildren(heading, confirmation, returnLink);
		} catch (_error) {
			submit.disabled = false;
			if (guestOrderStatus) {
				guestOrderStatus.setAttribute("role", "alert");
				guestOrderStatus.textContent = guestPendingPayment
					? "Zamówienie jest zapisane, ale nie udało się otworzyć płatności. Kliknij ponownie, aby wznowić płatność bez tworzenia nowego zamówienia."
					: "Nie udało się utworzyć zamówienia. Sprawdź dane lub spróbuj ponownie.";
				if (guestPendingPayment) {
					const paymentStatusLink = document.createElement("a");
					paymentStatusLink.href = `/platnosc-gosc?attempt=${encodeURIComponent(
						guestPendingPayment.attempt
					)}`;
					paymentStatusLink.textContent = " Sprawdź status płatności";
					guestOrderStatus.append(paymentStatusLink);
				}
			}
		}
	});
})();

// ACCOUNT-01b: app-owned Wishlist controls.  This boundary deliberately
// speaks only to the safe Website Item API; legacy wishlist controls and
// browser storage are not used.
(() => {
	"use strict";

	const WISHLIST_API = "/api/method/kuck_shop.accounts.wishlist_api.";
	const WISHLIST_TARGET = /^[^\u0000-\u001f\u007f]{1,140}$/u;
	const PUBLIC_ROUTE = /^\/[\p{L}\p{N}._~!$&'()*+,;=:@%\/-]{1,511}$/u;
	const FORBIDDEN_ROUTE_SEGMENTS = new Set(["api", "desk", "login", "logout", "private"]);
	let bound = false;
	let lastStateSignature = null;
	let refreshTimer = null;

	const isGuest = () => {
		const sessionStatus = document.body?.getAttribute("frappe-session-status");
		if (sessionStatus === "logged-out") return true;
		if (sessionStatus === "logged-in") return false;
		const user = window.frappe?.session?.user;
		return (
			!user ||
			user === "Guest" ||
			document.body?.getAttribute("frappe-session-status") === "logged-out"
		);
	};

	const safeTarget = (value) =>
		typeof value === "string" && value === value.trim() && WISHLIST_TARGET.test(value)
			? value
			: null;

	const safeRoute = (value) => {
		if (typeof value !== "string" || !value || value.includes("?") || value.includes("#"))
			return null;
		const path = value.startsWith("/") ? value : `/${value}`;
		if (!PUBLIC_ROUTE.test(path) || path.includes("//")) return null;
		const segments = path.slice(1).split("/");
		if (
			segments.some(
				(segment) =>
					!segment ||
					segment === "." ||
					segment === ".." ||
					FORBIDDEN_ROUTE_SEGMENTS.has(segment.toLowerCase())
			)
		)
			return null;
		return path;
	};
	const escapeHtml = (value) => {
		const text = String(value ?? "");
		return text.replace(
			/[&<>"']/g,
			(character) =>
				({
					"&": "&amp;",
					"<": "&lt;",
					">": "&gt;",
					'"': "&quot;",
					"'": "&#39;",
				}[character])
		);
	};

	const loginHref = (route) =>
		`/login?redirect-to=${encodeURIComponent(safeRoute(route) || "/wishlist")}`;

	const displayName = (item) => {
		const candidate = item?.web_item_name || item?.item_name;
		return typeof candidate === "string" && candidate.trim()
			? candidate.trim().slice(0, 240)
			: "produkt";
	};

	const renderCatalogControl = (item) => {
		const websiteItem = safeTarget(item?.name);
		const route = safeRoute(item?.route);
		if (!websiteItem || !route) return "";
		const name = displayName(item);
		const comparisonControl = () => window.kuckComparison?.renderCatalogControl?.(item) || "";
		if (isGuest()) {
			return `<a class="ks-wishlist-login-action ks-wishlist-card-action" data-kuck-wishlist-login
				href="${escapeHtml(loginHref(route))}" aria-label="Zaloguj się, aby zapisać ${escapeHtml(name)}">
				<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor"
					stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21.2l7.8-7.8a5.5 5.5 0 0 0 0-7.8Z"/>
				</svg><span class="sr-only">Zaloguj się, aby zapisać</span></a>${comparisonControl()}`;
		}
		return `<button class="ks-wishlist-action ks-wishlist-card-action" type="button"
			data-kuck-wishlist-action data-wishlist-action="add" data-website-item="${escapeHtml(websiteItem)}"
			aria-pressed="false" aria-label="Dodaj ${escapeHtml(name)} do listy życzeń">
			<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor"
				stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
				<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21.2l7.8-7.8a5.5 5.5 0 0 0 0-7.8Z"/>
			</svg><span class="sr-only">Dodaj do listy życzeń</span>
		</button>${comparisonControl()}`;
	};

	const csrfToken = () =>
		document.querySelector('meta[name="csrf-token"]')?.content ||
		window.frappe?.csrf_token ||
		"";

	const post = async (method, values = {}) => {
		const token = csrfToken();
		if (typeof token !== "string" || !token) throw new Error("CSRF_UNAVAILABLE");
		const body = new URLSearchParams();
		Object.entries(values).forEach(([key, value]) => body.set(key, String(value)));
		const response = await window.fetch(`${WISHLIST_API}${method}`, {
			method: "POST",
			credentials: "same-origin",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
				"X-Frappe-CSRF-Token": token,
			},
			body,
		});
		if (!response.ok) throw new Error("WISHLIST_REQUEST_REJECTED");
		const envelope = await response.json();
		const result = envelope?.message || envelope;
		if (!result || result.status !== "ok") throw new Error("WISHLIST_RESPONSE_INVALID");
		return result;
	};

	const countValue = (value) =>
		Number.isInteger(value) && value >= 0 && value <= 100 ? value : 0;

	const setHeaderCount = (value) => {
		const count = countValue(value);
		document.querySelectorAll("[data-kuck-wishlist-nav]").forEach((nav) => {
			const badge = nav.querySelector("[data-kuck-wishlist-count]");
			if (badge) {
				badge.textContent = String(count);
				badge.hidden = false;
				badge.setAttribute("aria-hidden", "false");
			}
			nav.setAttribute("aria-label", `Lista życzeń, ${count}`);
		});
	};

	const setStatus = (element, message, error = false) => {
		const status =
			element.closest(".ks-wishlist-page")?.querySelector("[data-kuck-wishlist-status]") ||
			document.querySelector("[data-kuck-wishlist-status]");
		if (!status) return;
		status.textContent = message;
		if (error) status.setAttribute("role", "alert");
		else status.setAttribute("role", "status");
	};

	const emptyState = () => {
		const section = document.createElement("section");
		section.className = "ks-wishlist-state ks-wishlist-state--empty";
		section.setAttribute("aria-labelledby", "wishlist-empty-after-remove-title");
		const heading = document.createElement("h2");
		heading.id = "wishlist-empty-after-remove-title";
		heading.textContent = "Twoja lista jest pusta";
		const message = document.createElement("p");
		message.textContent = "Przeglądaj produkty, aby dodać ulubione.";
		const link = document.createElement("a");
		link.className = "btn btn-primary";
		link.href = "/all-products";
		link.textContent = "Przeglądaj produkty";
		section.append(heading, message, link);
		return section;
	};

	const updateAction = (button, wished) => {
		button.dataset.wishlistAction = wished ? "remove" : "add";
		button.setAttribute("aria-pressed", String(wished));
		button.classList.toggle("ks-wishlist-action--remove", wished);
		button.classList.toggle("ks-wishlist-action--add", !wished);
		const srText = button.querySelector(".sr-only");
		if (srText) srText.textContent = wished ? "Usuń z listy życzeń" : "Dodaj do listy życzeń";
		button.setAttribute(
			"aria-label",
			wished ? "Usuń z listy życzeń" : "Dodaj do listy życzeń"
		);
	};

	const visibleWishlistTargets = () =>
		[...document.querySelectorAll("[data-kuck-wishlist-action][data-website-item]")]
			.map((button) => safeTarget(button.dataset.websiteItem))
			.filter(Boolean);

	const syncState = async () => {
		if (isGuest()) {
			setHeaderCount(0);
			return;
		}
		try {
			const result = await post("get_wishlist_state");
			const wished = new Set(
				(Array.isArray(result.website_items) ? result.website_items : [])
					.map(safeTarget)
					.filter(Boolean)
			);
			document
				.querySelectorAll("[data-kuck-wishlist-action][data-website-item]")
				.forEach((button) => {
					const target = safeTarget(button.dataset.websiteItem);
					if (target) updateAction(button, wished.has(target));
				});
			setHeaderCount(result.count);
		} catch (_error) {
			// A missing/expired session stays visually neutral; no private value is
			// inferred from the failed request.
			setHeaderCount(0);
		}
	};

	const scheduleStateRefresh = () => {
		const signature = visibleWishlistTargets().join("\u001f");
		if (signature === lastStateSignature) return;
		lastStateSignature = signature;
		if (refreshTimer) window.clearTimeout(refreshTimer);
		refreshTimer = window.setTimeout(() => {
			refreshTimer = null;
			syncState();
		}, 0);
	};

	const bind = () => {
		if (bound) return;
		bound = true;
		document.addEventListener("click", async (event) => {
			const target =
				event.target instanceof Element
					? event.target.closest("[data-kuck-wishlist-action][data-website-item]")
					: null;
			if (!(target instanceof HTMLButtonElement)) return;
			event.preventDefault();
			if (target.disabled || isGuest()) return;
			const websiteItem = safeTarget(target.dataset.websiteItem);
			const action = target.dataset.wishlistAction;
			if (!websiteItem || !["add", "remove"].includes(action)) return;
			target.disabled = true;
			setStatus(
				target,
				action === "remove" ? "Usuwamy z listy życzeń…" : "Zapisujemy na liście życzeń…"
			);
			try {
				const result = await post(
					action === "remove" ? "remove_wishlist_item" : "add_wishlist_item",
					{
						website_item: websiteItem,
					}
				);
				const wished = result.wished === true;
				updateAction(target, wished);
				setHeaderCount(result.count);
				if (action === "remove" && !wished) {
					const card = target.closest("[data-kuck-wishlist-card]");
					card?.remove();
					const list = document.querySelector("[data-kuck-wishlist-list]");
					if (list && !list.querySelector("[data-kuck-wishlist-card]")) {
						list.replaceWith(emptyState());
					}
				}
				setStatus(target, wished ? "Dodano do listy życzeń." : "Usunięto z listy życzeń.");
			} catch (_error) {
				setStatus(target, "Nie udało się zmienić listy życzeń. Spróbuj ponownie.", true);
			} finally {
				target.disabled = false;
			}
		});
		setHeaderCount(0);
		scheduleStateRefresh();
		const listing = document.querySelector("#product-listing");
		if (listing) {
			new MutationObserver(scheduleStateRefresh).observe(listing, {
				childList: true,
				subtree: true,
			});
		}
	};

	window.kuckWishlist = Object.freeze({ refresh: scheduleStateRefresh, renderCatalogControl });
	if (document.readyState === "loading")
		document.addEventListener("DOMContentLoaded", bind, { once: true });
	else bind();
})();

// COMPARE-01: account comparison uses the private API; guests keep a bounded
// comparison list for the current browser session.
(() => {
	"use strict";

	const COMPARISON_API = "/api/method/kuck_shop.accounts.comparison_api.";
	const COMPARISON_TARGET = /^[^\u0000-\u001f\u007f]{1,140}$/u;
	const PUBLIC_ROUTE = /^\/[\p{L}\p{N}._~!$&'()*+,;=:@%\/-]{1,511}$/u;
	const FORBIDDEN_ROUTE_SEGMENTS = new Set(["api", "desk", "login", "logout", "private"]);
	const GUEST_STORAGE_KEY = "kuck_guest_comparison";
	let bound = false;
	let lastStateSignature = null;
	let refreshTimer = null;

	const isGuest = () => {
		const sessionStatus = document.body?.getAttribute("frappe-session-status");
		if (sessionStatus === "logged-out") return true;
		if (sessionStatus === "logged-in") return false;
		const user = window.frappe?.session?.user;
		return (
			!user ||
			user === "Guest" ||
			document.body?.getAttribute("frappe-session-status") === "logged-out"
		);
	};

	const safeTarget = (value) =>
		typeof value === "string" && value === value.trim() && COMPARISON_TARGET.test(value)
			? value
			: null;

	const safeRoute = (value) => {
		if (typeof value !== "string" || !value || value.includes("?") || value.includes("#"))
			return null;
		const path = value.startsWith("/") ? value : `/${value}`;
		if (!PUBLIC_ROUTE.test(path) || path.includes("//")) return null;
		const segments = path.slice(1).split("/");
		if (
			segments.some(
				(segment) =>
					!segment ||
					segment === "." ||
					segment === ".." ||
					FORBIDDEN_ROUTE_SEGMENTS.has(segment.toLowerCase())
			)
		)
			return null;
		return path;
	};
	const safeComparisonImage = (value) =>
		typeof value === "string" &&
		value.length <= 512 &&
		(value.startsWith("/files/") || value.startsWith("/assets/"))
			? value
			: "";

	const escapeHtml = (value) => {
		const text = String(value ?? "");
		return text.replace(
			/[&<>"']/g,
			(character) =>
				({
					"&": "&amp;",
					"<": "&lt;",
					">": "&gt;",
					'"': "&quot;",
					"'": "&#39;",
				}[character])
		);
	};

	const loginHref = () => "/login?redirect-to=%2Fporownaj";

	const displayName = (item) => {
		const candidate = item?.web_item_name || item?.item_name;
		return typeof candidate === "string" && candidate.trim()
			? candidate.trim().slice(0, 240)
			: "produkt";
	};

	const renderCatalogControl = (item) => {
		const websiteItem = safeTarget(item?.name);
		const route = safeRoute(item?.route);
		if (!websiteItem || !route) return "";
		const name = displayName(item);
		if (isGuest()) {
			return `<button class="ks-comparison-action ks-comparison-card-action ks-comparison-action--add" type="button"
				data-kuck-comparison-action data-comparison-action="add" data-website-item="${escapeHtml(
					websiteItem
				)}"
				data-comparison-name="${escapeHtml(name)}" data-comparison-route="${escapeHtml(route)}"
				data-comparison-image="${escapeHtml(safeComparisonImage(item?.image || item?.image_url || ""))}"
				data-comparison-price="${escapeHtml(item?.formatted_price || item?.current_price || "")}"
				aria-pressed="false" aria-label="Dodaj ${escapeHtml(name)} do porównania">
				<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor"
					stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<path d="m16 3 4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16"/>
				</svg><span class="sr-only">Dodaj do porównania</span></button>`;
		}
		return `<button class="ks-comparison-action ks-comparison-card-action ks-comparison-action--add" type="button"
				data-kuck-comparison-action data-comparison-action="add" data-website-item="${escapeHtml(
					websiteItem
				)}"
				data-comparison-name="${escapeHtml(name)}"
				aria-pressed="false" aria-label="Dodaj ${escapeHtml(name)} do porównania">
				<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor"
					stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<path d="m16 3 4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16"/>
				</svg><span class="sr-only">Dodaj do porównania</span></button>`;
	};

	const csrfToken = () =>
		document.querySelector('meta[name="csrf-token"]')?.content ||
		window.frappe?.csrf_token ||
		"";

	const post = async (method, values = {}) => {
		const token = csrfToken();
		if (typeof token !== "string" || !token) throw new Error("CSRF_UNAVAILABLE");
		const body = new URLSearchParams();
		Object.entries(values).forEach(([key, value]) => body.set(key, String(value)));
		const response = await window.fetch(`${COMPARISON_API}${method}`, {
			method: "POST",
			credentials: "same-origin",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
				"X-Frappe-CSRF-Token": token,
			},
			body,
		});
		if (!response.ok) throw new Error("COMPARISON_REQUEST_REJECTED");
		const envelope = await response.json();
		const result = envelope?.message || envelope;
		if (!result || result.status !== "ok") throw new Error("COMPARISON_RESPONSE_INVALID");
		return result;
	};

	const countValue = (value) =>
		Number.isInteger(value) && value >= 0 && value <= 4 ? value : 0;

	const setHeaderCount = (value) => {
		const count = countValue(value);
		document.querySelectorAll("[data-kuck-comparison-nav]").forEach((nav) => {
			const badge = nav.querySelector("[data-kuck-comparison-count]");
			const link = nav.matches?.('a[href="/porownaj"]')
				? nav
				: nav.querySelector('a[href="/porownaj"]');
			if (badge) {
				badge.textContent = String(count);
				badge.hidden = false;
				badge.setAttribute("aria-hidden", "false");
			}
			link?.setAttribute(
				"aria-label",
				count ? `Porównaj produkty, ${count}` : "Porównaj produkty"
			);
		});
	};

	let pageRefresh = null;
	const refreshFromPage = () => {
		if (typeof window.fetch !== "function") return null;
		if (pageRefresh) return pageRefresh;
		pageRefresh = window
			.fetch("/porownaj", {
				credentials: "same-origin",
				cache: "no-store",
			})
			.then((response) => (response.ok ? response.text() : ""))
			.then((html) => {
				const count = (html.match(/data-kuck-comparison-card/g) || []).length;
				if (count > 0) setHeaderCount(count);
			})
			.catch(() => {})
			.finally(() => {
				pageRefresh = null;
			});
		return pageRefresh;
	};

	let tableRefresh = null;
	const refreshComparisonTable = () => {
		if (typeof window.fetch !== "function" || typeof DOMParser !== "function") return null;
		if (tableRefresh) return tableRefresh;
		tableRefresh = window
			.fetch("/porownaj", {
				credentials: "same-origin",
				cache: "no-store",
			})
			.then((response) => (response.ok ? response.text() : ""))
			.then((html) => {
				const parsed = new DOMParser().parseFromString(html, "text/html");
				const current = document.querySelector(
					".ks-comparison-table, [data-kuck-comparison-table]"
				);
				const next = parsed.querySelector(
					".ks-comparison-table, [data-kuck-comparison-table]"
				);
				if (next) {
					if (current) current.replaceWith(next);
					else
						document
							.querySelector(
								".ks-comparison-results, [data-kuck-comparison-results]"
							)
							?.append(next);
				} else current?.remove();
			})
			.catch(() => {})
			.finally(() => {
				tableRefresh = null;
			});
		return tableRefresh;
	};

	const setStatus = (element, message, error = false) => {
		const status =
			element
				?.closest?.(".ks-comparison-page, [data-kuck-comparison-page]")
				?.querySelector("[data-kuck-comparison-status]") ||
			document.querySelector("[data-kuck-comparison-status]");
		if (!status) return;
		status.textContent = message;
		status.setAttribute("role", error ? "alert" : "status");
	};

	const emptyState = () => {
		const section = document.createElement("section");
		section.className = "ks-comparison-state ks-comparison-state--empty";
		section.setAttribute("data-kuck-comparison-state", "");
		section.setAttribute("aria-labelledby", "comparison-empty-after-remove-title");
		const heading = document.createElement("h2");
		heading.id = "comparison-empty-after-remove-title";
		heading.textContent = "Nie porównujesz jeszcze żadnych produktów";
		const message = document.createElement("p");
		message.textContent = "Przeglądaj produkty, aby dodać je do porównania.";
		const link = document.createElement("a");
		link.className = "btn btn-primary";
		link.href = "/all-products";
		link.textContent = "Przeglądaj produkty";
		section.append(heading, message, link);
		return section;
	};

	const updateAction = (button, compared) => {
		button.dataset.comparisonAction = compared ? "remove" : "add";
		button.setAttribute("aria-pressed", String(compared));
		button.classList.toggle("ks-comparison-action--remove", compared);
		button.classList.toggle("ks-comparison-action--add", !compared);
		const name = button.dataset.comparisonName || "produkt";
		const label = compared ? "Usuń z porównania" : "Dodaj do porównania";
		const srText = button.querySelector(".sr-only, .kr-sr-only");
		if (srText) srText.textContent = label;
		else if (
			!button.classList.contains("ks-comparison-card__remove") &&
			!button.closest(".kr-page")
		)
			button.textContent = label;
		button.setAttribute(
			"aria-label",
			compared ? `Usuń ${name} z porównania` : `Dodaj ${name} do porównania`
		);
	};

	const visibleTargets = () =>
		[...document.querySelectorAll("[data-kuck-comparison-action][data-website-item]")]
			.map((button) => safeTarget(button.dataset.websiteItem))
			.filter(Boolean);

	const readGuestItems = () => {
		if (!isGuest()) return [];
		try {
			const value = JSON.parse(window.sessionStorage.getItem(GUEST_STORAGE_KEY) || "[]");
			return Array.isArray(value)
				? value
						.slice(0, 4)
						.filter((item) => safeTarget(item?.websiteItem) && safeRoute(item?.route))
						.map((item) => ({ ...item, image: safeComparisonImage(item?.image) }))
				: [];
		} catch (_error) {
			return [];
		}
	};

	const writeGuestItems = (items) => {
		try {
			window.sessionStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(items.slice(0, 4)));
		} catch (_error) {
			// Storage can be unavailable; the current comparison still remains usable.
		}
	};

	let guestRenderSequence = 0;
	const renderGuestPage = async () => {
		const main = document.querySelector(".ks-comparison-page, [data-kuck-comparison-page]");
		if (!main || !isGuest()) return;
		const sequence = ++guestRenderSequence;
		const items = readGuestItems();
		const old = main.querySelector(
			".ks-comparison-state, .ks-comparison-results, [data-kuck-comparison-state], [data-kuck-comparison-results]"
		);
		if (old) old.remove();
		const section = document.createElement("section");
		section.className = "ks-comparison-state";
		section.setAttribute("data-kuck-comparison-state", "");
		main.append(section);
		setHeaderCount(items.length);
		if (!items.length) {
			section.replaceWith(emptyState());
			return;
		}
		section.setAttribute("aria-busy", "true");
		section.setAttribute("role", "status");
		section.textContent = "Wczytujemy parametry porównywanych produktów…";
		try {
			const query = new URLSearchParams({
				website_items: JSON.stringify(items.map((item) => item.websiteItem)),
			});
			const response = await window.fetch(
				`${COMPARISON_API}get_public_comparison?${query}`,
				{
					method: "GET",
					credentials: "same-origin",
					cache: "no-store",
				}
			);
			if (!response.ok) throw new Error("PUBLIC_COMPARISON_UNAVAILABLE");
			const result = (await response.json())?.message;
			if (
				result?.status !== "ok" ||
				typeof result.html !== "string" ||
				result.html.length > 250000 ||
				!Array.isArray(result.website_items) ||
				result.website_items.length > 4
			) {
				throw new Error("PUBLIC_COMPARISON_INVALID");
			}
			const requested = new Set(items.map((item) => item.websiteItem));
			if (
				result.website_items.some(
					(value) => !safeTarget(value) || !requested.has(value)
				) ||
				new Set(result.website_items).size !== result.website_items.length ||
				result.count !== result.website_items.length
			) {
				throw new Error("PUBLIC_COMPARISON_INVALID");
			}
			const parsed = new DOMParser().parseFromString(result.html, "text/html");
			const next = parsed.querySelector(
				".ks-comparison-results, .ks-comparison-state, [data-kuck-comparison-results], [data-kuck-comparison-state]"
			);
			if (!next) throw new Error("PUBLIC_COMPARISON_INVALID");
			if (sequence !== guestRenderSequence || !isGuest()) return;
			const included = new Set(result.website_items.map(safeTarget).filter(Boolean));
			const remaining = items.filter((item) => included.has(item.websiteItem));
			writeGuestItems(remaining);
			section.replaceWith(next);
			setHeaderCount(remaining.length);
			if (remaining.length < items.length) {
				setStatus(next, "Produkty wycofane z oferty zostały usunięte z porównania.");
			}
		} catch (_error) {
			if (sequence !== guestRenderSequence || !isGuest()) return;
			section.removeAttribute("aria-busy");
			section.setAttribute("role", "alert");
			section.textContent =
				"Nie udało się wczytać parametrów produktów. Twój wybór został zachowany. ";
			const retry = document.createElement("a");
			retry.href = "/porownaj";
			retry.textContent = "Spróbuj ponownie";
			section.append(retry);
		}
	};

	const syncState = async () => {
		if (isGuest()) {
			setHeaderCount(readGuestItems().length);
			renderGuestPage();
			return;
		}
		try {
			const result = await post("get_comparison_state");
			const compared = new Set(
				(Array.isArray(result.website_items) ? result.website_items : [])
					.map(safeTarget)
					.filter(Boolean)
			);
			document
				.querySelectorAll("[data-kuck-comparison-action][data-website-item]")
				.forEach((button) => {
					const target = safeTarget(button.dataset.websiteItem);
					if (target) updateAction(button, compared.has(target));
				});
			setHeaderCount(result.count);
			if (countValue(result.count) === 0) refreshFromPage();
		} catch (_error) {
			setHeaderCount(0);
			refreshFromPage();
		}
	};

	const scheduleStateRefresh = () => {
		const signature = visibleTargets().join("\u001f");
		if (signature === lastStateSignature) return;
		lastStateSignature = signature;
		if (refreshTimer) window.clearTimeout(refreshTimer);
		refreshTimer = window.setTimeout(() => {
			refreshTimer = null;
			syncState();
		}, 0);
	};
	const focusComparisonIntent = () => {
		if (window.location.hash !== "#kuck-compare") return;
		window.setTimeout(() => {
			const button = document.querySelector(
				"[data-kuck-comparison-action][data-comparison-action='add'][data-website-item]"
			);
			if (button instanceof HTMLButtonElement) button.focus({ preventScroll: false });
		}, 0);
	};

	const bind = () => {
		if (bound) return;
		bound = true;
		document.addEventListener("click", async (event) => {
			const source = event.target;
			const target =
				source && typeof source.closest === "function"
					? source.closest("[data-kuck-comparison-action][data-website-item]")
					: null;
			if (!target || target.tagName !== "BUTTON") return;
			event.preventDefault();
			if (target.disabled) return;
			const websiteItem = safeTarget(target.dataset.websiteItem);
			const action = target.dataset.comparisonAction;
			if (!websiteItem || !["add", "remove"].includes(action)) return;
			target.disabled = true;
			if (isGuest()) {
				const items = readGuestItems();
				const next =
					action === "remove"
						? items.filter((item) => item.websiteItem !== websiteItem)
						: [
								{
									websiteItem,
									name: target.dataset.comparisonName || "produkt",
									route: safeRoute(target.dataset.comparisonRoute) || "/",
									image: target.dataset.comparisonImage || "",
									price: target.dataset.comparisonPrice || "",
								},
								...items.filter((item) => item.websiteItem !== websiteItem),
						  ];
				writeGuestItems(next);
				updateAction(target, action === "remove" ? false : true);
				setHeaderCount(next.length);
				if (action === "remove") {
					target.closest("[data-kuck-comparison-card]")?.remove();
					if (document.querySelector(".ks-comparison-page, [data-kuck-comparison-page]"))
						renderGuestPage();
				} else setStatus(target, "Dodano do porównania.");
				target.disabled = false;
				return;
			}
			setStatus(
				target,
				action === "remove" ? "Usuwamy z porównania…" : "Dodajemy do porównania…"
			);
			try {
				const result = await post(
					action === "remove" ? "remove_comparison_item" : "add_comparison_item",
					{ website_item: websiteItem }
				);
				const compared = result.compared === true;
				updateAction(target, compared);
				setHeaderCount(result.count);
				if (action === "remove" && !compared) {
					const card = target.closest("[data-kuck-comparison-card]");
					card?.remove();
					const list = document.querySelector("[data-kuck-comparison-list]");
					if (list && !list.querySelector("[data-kuck-comparison-card]"))
						list.replaceWith(emptyState());
					refreshComparisonTable();
				}
				setStatus(
					target,
					result.changed === true
						? "Porównywarka została zaktualizowana."
						: "Nie udało się zmienić porównywarki. Spróbuj ponownie.",
					result.changed !== true
				);
			} catch (_error) {
				setStatus(target, "Nie udało się zmienić porównywarki. Spróbuj ponownie.", true);
			} finally {
				target.disabled = false;
			}
		});
		setHeaderCount(0);
		scheduleStateRefresh();
		focusComparisonIntent();
		const listing = document.querySelector("#product-listing");
		if (listing) {
			new MutationObserver(scheduleStateRefresh).observe(listing, {
				childList: true,
				subtree: true,
			});
		}
	};

	window.kuckComparison = Object.freeze({ refresh: scheduleStateRefresh, renderCatalogControl });
	if (document.readyState === "loading")
		document.addEventListener("DOMContentLoaded", bind, { once: true });
	else bind();
})();

// HOME-CARDS-01: hydrate moderator-selected Item Card Group products with the
// same bounded public commerce projection used by the catalogue. The original
// links and images remain a useful fallback if the read-only request fails.
(() => {
	"use strict";

	const API = "/api/method/kuck_shop.storefront.webshop_catalog.get_home_product_cards";
	const MAX_CARDS = 12;
	const PUBLIC_ROUTE = /^[\p{L}\p{N}._~!$&'()*+,;=:@%\/-]{1,512}$/u;
	const PUBLIC_VALUE = /^[^\u0000-\u001f\u007f<>]{1,500}$/u;
	const PRIVATE_SEGMENTS = new Set([
		"api",
		"app",
		"desk",
		"login",
		"logout",
		"private",
		"account",
		"konto",
		"checkout",
		"cart",
		"koszyk",
	]);

	const isHomePage = () => {
		const path = window.location.pathname.replace(/\/+$/, "") || "/";
		return path === "/home" || document.body?.dataset.path === "kuck-home";
	};

	const isGuest = () => {
		const status = document.body?.getAttribute("frappe-session-status");
		if (status === "logged-out") return true;
		if (status === "logged-in") return false;
		return !window.frappe?.session?.user || window.frappe.session.user === "Guest";
	};

	const safeText = (value, maximum = 500) =>
		typeof value === "string" && value.length <= maximum && PUBLIC_VALUE.test(value)
			? value.trim()
			: null;

	const safeRoute = (value) => {
		if (typeof value !== "string" || !PUBLIC_ROUTE.test(value)) return null;
		const route = value.replace(/^\/+|\/+$/g, "");
		const segments = route.split("/");
		if (
			!route ||
			route.includes("//") ||
			segments.some((segment) => !segment || segment === "." || segment === "..") ||
			PRIVATE_SEGMENTS.has(segments[0].toLowerCase())
		)
			return null;
		return route;
	};

	const routeFromCard = (card) => {
		const link = card.querySelector(
			".card-img-container a[href], .product-title a[href], a[href]"
		);
		if (!(link instanceof HTMLAnchorElement)) return null;
		try {
			const url = new URL(link.getAttribute("href") || "", window.location.origin);
			if (url.origin !== window.location.origin || url.search || url.hash) return null;
			return safeRoute(decodeURIComponent(url.pathname));
		} catch (_error) {
			return null;
		}
	};

	const icon = (kind) => {
		const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		svg.setAttribute("viewBox", "0 0 24 24");
		svg.setAttribute("aria-hidden", "true");
		svg.setAttribute("focusable", "false");
		svg.setAttribute("fill", "none");
		svg.setAttribute("stroke", "currentColor");
		svg.setAttribute("stroke-width", "2");
		svg.setAttribute("stroke-linecap", "round");
		svg.setAttribute("stroke-linejoin", "round");
		const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
		path.setAttribute(
			"d",
			kind === "wishlist"
				? "M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21.2l7.8-7.8a5.5 5.5 0 0 0 0-7.8Z"
				: kind === "comparison"
				? "m16 3 4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16"
				: "M6 7h12l-1 13H7L6 7Zm3 0V5a3 3 0 0 1 6 0v2"
		);
		svg.append(path);
		return svg;
	};

	const visuallyHidden = (text) => {
		const span = document.createElement("span");
		span.className = "sr-only";
		span.textContent = text;
		return span;
	};

	const homeAction = (kind, item) => {
		const label = safeText(item.web_item_name, 240) || "produkt";
		const guest = isGuest();
		const control = document.createElement(kind === "wishlist" && guest ? "a" : "button");
		control.className =
			kind === "wishlist"
				? `ks-wishlist-${guest ? "login" : "action"} ks-wishlist-card-action`
				: `ks-comparison-${
						guest ? "login" : "action"
				  } ks-comparison-card-action ks-comparison-action--add`;
		if (guest) {
			if (kind === "wishlist") {
				control.href = `/login?redirect-to=${encodeURIComponent(`/${item.route}`)}`;
				control.dataset.kuckWishlistLogin = "";
			} else {
				control.type = "button";
				control.dataset.kuckComparisonAction = "";
				control.dataset.comparisonAction = "add";
				control.dataset.websiteItem = item.name;
				control.dataset.comparisonName = label;
				control.dataset.comparisonRoute = `/${item.route}`;
				control.dataset.comparisonImage = item.image || item.image_url || "";
				control.dataset.comparisonPrice = item.formatted_price || item.current_price || "";
			}
		} else {
			control.type = "button";
			control.dataset.websiteItem = item.name;
			control.setAttribute("aria-pressed", "false");
			if (kind === "wishlist") {
				control.dataset.kuckWishlistAction = "";
				control.dataset.wishlistAction = "add";
			} else {
				control.dataset.kuckComparisonAction = "";
				control.dataset.comparisonAction = "add";
				control.dataset.comparisonName = label;
			}
		}
		const actionLabel = guest
			? `${
					kind === "wishlist"
						? `Zaloguj się, aby zapisać ${label}`
						: `Dodaj ${label} do porównania`
			  }`
			: `Dodaj ${label} ${kind === "wishlist" ? "do listy życzeń" : "do porównania"}`;
		control.setAttribute("aria-label", actionLabel);
		control.append(icon(kind), visuallyHidden(actionLabel));
		return control;
	};

	const appendActions = (card, item) => {
		const media = card.querySelector(".card-img-container");
		if (!(media instanceof HTMLElement) || media.querySelector(".ks-product-card-actions"))
			return;
		const actions = document.createElement("div");
		actions.className = "ks-product-card-actions";
		actions.setAttribute("role", "group");
		actions.setAttribute("aria-label", "Akcje produktu");
		actions.append(homeAction("wishlist", item), homeAction("comparison", item));
		media.append(actions);
	};

	const appendPrice = (body, item) => {
		const formatted = safeText(item.formatted_price, 120);
		const projection = item.kuck_price_presentation;
		const reduction =
			projection?.is_reduction === true &&
			safeText(projection.current_price_text, 120) &&
			safeText(projection.previous_price_text, 120) &&
			safeText(projection.lowest_price_label, 500)
				? projection
				: null;
		const current = reduction ? safeText(reduction.current_price_text, 120) : formatted;
		if (!current) return;
		const price = document.createElement("div");
		price.className = "product-price";
		price.setAttribute("itemprop", "offers");
		price.textContent = current;
		if (reduction) {
			const previous = document.createElement("small");
			previous.className = "striked-price";
			const strike = document.createElement("s");
			strike.textContent = safeText(reduction.previous_price_text, 120);
			previous.append(strike);
			const lowest = document.createElement("small");
			lowest.className = "ks-price__lowest";
			lowest.textContent = safeText(reduction.lowest_price_label, 500);
			price.append(previous, lowest);
		}
		body.append(price);
	};

	const appendInstallments = (body, item) => {
		const value = item.kuck_installments;
		if (
			value?.enabled !== true ||
			value.selection_enabled !== false ||
			value.selection_state !== "HOLD" ||
			value.placement !== "listing"
		)
			return;
		const label = safeText(value.label);
		const summary = safeText(value.summary);
		const amount = safeText(value.payment_amount_text);
		const total = safeText(value.total_amount_text);
		if (
			!label ||
			!summary ||
			!amount ||
			!total ||
			!Number.isInteger(value.payment_count) ||
			value.payment_count < 2 ||
			value.payment_count > 120
		)
			return;
		const section = document.createElement("section");
		section.className = "ks-installments";
		const strong = document.createElement("strong");
		strong.className = "ks-installments__label";
		strong.textContent = label;
		const summaryNode = document.createElement("span");
		summaryNode.className = "ks-installments__summary";
		summaryNode.textContent = summary;
		const amountNode = document.createElement("span");
		amountNode.className = "ks-installments__amount";
		amountNode.textContent = `${value.payment_count} rat × ${amount}`;
		const totalNode = document.createElement("span");
		totalNode.className = "ks-installments__total";
		totalNode.textContent = `Łącznie: ${total}`;
		section.append(strong, summaryNode, amountNode, totalNode);
		body.append(section);
	};

	const appendAvailability = (body, item, settings) => {
		if (!settings.show_stock_availability || item.has_variants) return;
		const label = item.on_backorder
			? "Dostępny na zamówienie"
			: !item.in_stock
			? "Niedostępny online"
			: null;
		if (!label) return;
		const status = document.createElement("span");
		status.className = "out-of-stock product-availability";
		status.textContent = label;
		body.append(status);
	};

	const appendCartAction = (body, item, settings) => {
		const action = document.createElement("div");
		action.className = "primary-action-section";
		if (item.has_variants) {
			const link = document.createElement("a");
			link.className = "btn btn-sm btn-explore-variants";
			link.href = `/${item.route}`;
			link.textContent = "Wybierz wariant";
			action.append(link);
			body.append(action);
			return;
		}
		const hasPrice = Boolean(
			safeText(item.formatted_price, 120) ||
				safeText(item.kuck_price_presentation?.current_price_text, 120)
		);
		const isOrderable = item.on_backorder || item.in_stock;
		if (!settings.enabled || !hasPrice || !isOrderable) return;
		const guest = isGuest();
		const itemCode = safeText(item.item_code, 140);
		const route = safeRoute(item.route);
		if (!guest && !itemCode) return;
		if (guest && !itemCode) {
			if (!route) return;
			const button = document.createElement("button");
			button.type = "button";
			button.className = "btn btn-sm btn-primary btn-add-to-cart-list ks-home-cart-link";
			button.dataset.kuckProductRoute = `/${route}`;
			button.setAttribute("aria-label", "Dodaj do koszyka");
			button.append(icon("cart"), visuallyHidden("Dodaj do koszyka"));
			action.append(button);
			body.append(action);
			return;
		}
		const button = document.createElement("button");
		button.type = "button";
		button.className = `btn btn-sm btn-primary btn-add-to-cart-list${
			item.in_cart ? " hidden" : ""
		}`;
		button.setAttribute("aria-label", "Dodaj do koszyka");
		button.dataset.itemCode = itemCode;
		button.append(icon("cart"), visuallyHidden("Dodaj do koszyka"));
		const cart = document.createElement("a");
		cart.className = `btn btn-sm btn-primary go-to-cart-grid${item.in_cart ? "" : " hidden"}`;
		cart.href = "/cart";
		cart.textContent = "Przejdź do koszyka";
		action.append(button, cart);
		body.append(action);
	};

	const enhanceCard = (card, item, settings) => {
		if (card.dataset.kuckHomeCommerceReady === "true") return;
		const body = card.querySelector(".card-body");
		if (!(body instanceof HTMLElement)) return;
		card.dataset.kuckHomeCommerceReady = "true";
		card.classList.add("ks-home-commerce-card");
		appendActions(card, item);
		appendPrice(body, item);
		appendInstallments(body, item);
		appendAvailability(body, item, settings);
		appendCartAction(body, item, settings);
	};

	const enhanceProductShelfCarousel = (shelf) => {
		if (!(shelf instanceof HTMLElement) || shelf.dataset.kuckProductCarouselReady === "true")
			return;
		const header = shelf.querySelector(":scope > .item-group-header");
		const row = shelf.querySelector(":scope > .row");
		if (!(header instanceof HTMLElement) || !(row instanceof HTMLElement)) return;
		const cards = [...row.querySelectorAll(":scope > .item-card")];
		if (cards.length < 2) return;
		const heading = header.querySelector("h2, h3");
		if (heading?.textContent?.trim() === "Nowości w katalogu") {
			heading.textContent = "Nowości";
		}
		const label = heading?.textContent?.trim() || "Polecane produkty";
		shelf.classList.add(
			"ks-home-featured",
			"ks-home-featured--carousel",
			"ks-home-product-shelf--carousel"
		);
		header.classList.add("ks-home-featured__header");
		const controls = document.createElement("div");
		controls.className = "ks-home-featured__controls";
		controls.setAttribute("aria-label", `Nawigacja: ${label}`);
		for (const [direction, copy, arrow] of [
			["prev", "Poprzednie", "←"],
			["next", "Następne", "→"],
		]) {
			const button = document.createElement("button");
			button.className = "ks-home-featured__control";
			button.type = "button";
			button.setAttribute(`data-kuck-product-${direction}`, "");
			button.setAttribute("aria-label", `${copy}: ${label}`);
			const icon = document.createElement("span");
			icon.setAttribute("aria-hidden", "true");
			icon.textContent = arrow;
			button.append(icon);
			controls.append(button);
		}
		header.append(controls);
		const viewport = document.createElement("div");
		viewport.className = "ks-home-featured__viewport";
		viewport.dataset.kuckProductViewport = "";
		viewport.tabIndex = 0;
		viewport.setAttribute("role", "region");
		viewport.setAttribute("aria-label", label);
		row.classList.add("ks-home-featured__track");
		row.dataset.kuckProductTrack = "";
		row.style.setProperty("--ks-product-count", String(cards.length));
		row.parentNode?.insertBefore(viewport, row);
		viewport.append(row);
		shelf.dataset.kuckProductCarousel = "";
		window.kuckProductCarouselInit?.(shelf);
	};

	const load = async () => {
		if (!isHomePage()) return;
		const shelves = [...document.querySelectorAll(".item-card-group-section")];
		shelves.forEach((shelf) => enhanceProductShelfCarousel(shelf));
		const cardRoutes = new Map();
		shelves.forEach((shelf) => {
			shelf.classList.add("ks-home-product-shelf");
			shelf.setAttribute("aria-busy", "true");
			shelf.querySelectorAll(".item-card").forEach((card) => {
				const route = routeFromCard(card);
				if (!route) return;
				if (!cardRoutes.has(route)) {
					if (cardRoutes.size >= MAX_CARDS) return;
					cardRoutes.set(route, []);
				}
				cardRoutes.get(route).push(card);
			});
		});
		const routes = [...cardRoutes.keys()];
		if (!routes.length) {
			shelves.forEach((shelf) => shelf.setAttribute("aria-busy", "false"));
			return;
		}
		try {
			const query = new URLSearchParams({ routes: JSON.stringify(routes) });
			const response = await window.fetch(`${API}?${query}`, {
				credentials: "same-origin",
				headers: { Accept: "application/json" },
			});
			if (!response.ok) throw new Error("HOME_PRODUCT_CARDS_REQUEST_REJECTED");
			const result = (await response.json())?.message;
			if (!result || !Array.isArray(result.items) || !result.settings) {
				throw new Error("HOME_PRODUCT_CARDS_RESPONSE_INVALID");
			}
			const settings = {
				allow_items_not_in_stock: result.settings.allow_items_not_in_stock === true,
				enable_checkout: result.settings.enable_checkout === true,
				enabled: result.settings.enabled === true,
				show_stock_availability: result.settings.show_stock_availability === true,
			};
			result.items.forEach((item) => {
				const route = safeRoute(item?.route);
				const name = safeText(item?.name, 140);
				if (!route || !name || !cardRoutes.has(route)) return;
				const publicItem = {
					...item,
					has_variants: item.has_variants === true,
					in_cart: item.in_cart === true,
					in_stock: item.in_stock === true,
					name,
					on_backorder: item.on_backorder === true,
					route,
				};
				cardRoutes.get(route).forEach((card) => enhanceCard(card, publicItem, settings));
			});
			if (
				document.body.dataset.ksHomeCartBound !== "true" &&
				window.webshop?.webshop?.shopping_cart?.bind_add_to_cart_action
			) {
				document.body.dataset.ksHomeCartBound = "true";
				window.webshop.webshop.shopping_cart.bind_add_to_cart_action();
			}
			window.kuckQuickView?.refresh?.();
			window.kuckWishlist?.refresh?.();
			window.kuckComparison?.refresh?.();
		} catch (_error) {
			// The server-rendered links and images remain usable as the fallback.
		} finally {
			shelves.forEach((shelf) => shelf.setAttribute("aria-busy", "false"));
		}
	};

	if (document.readyState === "loading")
		document.addEventListener("DOMContentLoaded", load, { once: true });
	else load();
})();
